// Zeiterfassung / Anwesenheit (Stempeluhr) — reine Domänenlogik.
//
// FIX B-5 (UTC-Mitternacht): Der Altcode gruppierte die Roh-Stempel per UTC-Tag
// (String(ts).slice(0,10)) BEVOR er sie paarte. Eine Schicht über die UTC-Mitternacht
// zerfiel so in zwei Tages-Eimer mit je einem unpaarigen Stempel → 0 Sekunden.
//
// Lösung: auf ABSOLUTEN Unix-Timestamps (epoch ms) paaren — das ist mitternachts-agnostisch.
// Für Tagesberichte werden geschlossene Segmente an der LOKALEN Mitternacht (Zeitzone) gesplittet,
// damit Statistik/Timesheet mit dem (lokalen) Monatsnachweis übereinstimmen. Vergessenes
// Ausstempeln (überlanges Segment) wird gekappt und als Anomalie gemeldet statt als Phantom gezählt.

export type StampKind = 'kommen' | 'gehen' | 'pause_start' | 'pause_ende'
export type AttStatus = 'aus' | 'an' | 'pause'

/** Ein Stempel mit ABSOLUTEM Zeitstempel (Unix epoch in Millisekunden). */
export interface Stamp {
  kind: StampKind
  ts: number
  /** DB-id (autoincrement) — Tiebreaker bei Sekunden-Gleichstand von ts/effective_ts (Ordnung (ts, id), R2/1.4).
   *  Optional: Aufrufer ohne id verhalten sich wie bisher (stabile Sortierung allein nach ts). */
  id?: number
}

export const ATT_KINDS: readonly StampKind[] = ['kommen', 'gehen', 'pause_start', 'pause_ende']

/** Erlaubte Stempel-Übergänge je aktuellem Status (verhindert Gehen-vor-Kommen, Doppel-Kommen, Pausen-Überlappung). */
export const ATT_ALLOWED: Record<StampKind, AttStatus[]> = {
  kommen: ['aus'],
  // Gehen aus Pause erlaubt (R2/1.3): beendet die Pause implizit. workedSegments zählt keine Pausenzeit als Arbeit,
  // die Endpause (pause_start→gehen) ist zudem KEINE anrechenbare §4-Pause (Bewertung in der Flag-Engine, R4).
  gehen: ['an', 'pause'],
  pause_start: ['an'],
  pause_ende: ['pause'],
}

export const DEFAULT_TIME_ZONE = 'Europe/Berlin'
/** Längstes plausibles zusammenhängendes Arbeitssegment. Längeres = vergessenes Ausstempeln → nicht zählen. */
export const DEFAULT_MAX_SEGMENT_SECONDS = 16 * 3600
/** Schichtfenster für die Status-Ermittlung (deckt Spät-/Nachtschicht über Mitternacht ab). */
export const DEFAULT_STATUS_WINDOW_HOURS = 18

const HOUR_MS = 3600 * 1000
const DAY_MS = 24 * HOUR_MS

const OPENING: ReadonlySet<StampKind> = new Set<StampKind>(['kommen', 'pause_ende'])
const CLOSING: ReadonlySet<StampKind> = new Set<StampKind>(['gehen', 'pause_start'])

function sortByTs(stamps: readonly Stamp[]): Stamp[] {
  // (ts, id): id bricht den Sekunden-Gleichstand deterministisch (effective_ts ist sekunden-genau, R2/1.4).
  // id-los (Bestandsaufrufer) == bisheriges Verhalten: stabile Sortierung allein nach ts.
  return [...stamps].sort((a, b) => a.ts - b.ts || (a.id ?? 0) - (b.id ?? 0))
}

/** Wandelt einen DB-UTC-Zeitstempel ('YYYY-MM-DD HH:MM:SS') in epoch ms. Zahl wird unverändert zurückgegeben. */
export function parseDbTimestamp(value: string | number | null | undefined): number {
  if (typeof value === 'number') return value
  if (!value) return Number.NaN
  return Date.parse(String(value).replace(' ', 'T') + 'Z')
}

/** Aktueller Anwesenheits-Status anhand des jüngsten Stempels. */
export function attendanceStatus(stamps: readonly Stamp[]): AttStatus {
  const last = sortByTs(stamps).at(-1)
  if (!last) return 'aus'
  if (last.kind === 'kommen' || last.kind === 'pause_ende') return 'an'
  if (last.kind === 'pause_start') return 'pause'
  return 'aus'
}

/** Status aus dem rollenden Schichtfenster (ältere Stempel ignorieren) — wie der Stempel-Guard im Altcode. */
export function currentStatus(
  stamps: readonly Stamp[],
  nowMs: number,
  windowHours: number = DEFAULT_STATUS_WINDOW_HOURS,
): AttStatus {
  const cutoff = nowMs - windowHours * HOUR_MS
  return attendanceStatus(stamps.filter((s) => s.ts >= cutoff))
}

/** Ist dieser Stempel im aktuellen Status erlaubt? */
export function canStamp(current: AttStatus, kind: StampKind): boolean {
  return ATT_ALLOWED[kind].includes(current)
}

// ===== Provenienz-Klassifikation (R2/1.3): der SERVER leitet source allein aus |stamped_at − now| ab =====
/** ≤ 5 min Abstand → Live-Stempel (OCC-Pfad, ts = Server-Zeit maßgeblich). */
export const STAMP_LIVE_WINDOW_MS = 5 * 60 * 1000
/** ≤ 72 h zurück → Nachzügler (outbox_replay); älter → Auto-Korrekturantrag (kein stiller Backdatierungs-Endpoint). */
export const STAMP_LATECOMER_MAX_MS = 72 * HOUR_MS

export type StampSource = 'live' | 'outbox_replay' | 'nachtrag'

export type StampProvenance =
  | { source: 'live'; stampedAtMs: null; skewClamped: boolean }
  | { source: 'outbox_replay'; stampedAtMs: number }
  | { source: 'correction'; stampedAtMs: number }

/**
 * Klassifiziert einen Stempel-Versuch anhand der Client-Stempelzeit (stamped_at) gegen die Server-Zeit.
 * REIN & I/O-frei — die eine Wahrheit für `source` (NIE Client-Deklaration). Grenzen unten inklusiv:
 *  - stampedAt fehlt/ungültig → live (stampedAtMs=null → effective_ts=ts; exakt heutiges Verhalten, Regressions-Anker).
 *  - stampedAt in der Zukunft → live, auf now geclampt (stampedAtMs=null); skewClamped bei > 5 min Vorlauf; NIE ablehnen.
 *  - Abstand ≤ 5 min → live (stampedAtMs=null).
 *  - 5 min < Abstand ≤ 72 h → outbox_replay (stampedAtMs = historische Position).
 *  - > 72 h → correction (Auto-Korrekturantrag, kein Insert).
 */
export function classifyStampProvenance(stampedAtMs: number | null | undefined, serverNowMs: number): StampProvenance {
  if (stampedAtMs == null || !Number.isFinite(stampedAtMs)) return { source: 'live', stampedAtMs: null, skewClamped: false }
  const delta = serverNowMs - stampedAtMs
  if (delta < 0) return { source: 'live', stampedAtMs: null, skewClamped: stampedAtMs - serverNowMs > STAMP_LIVE_WINDOW_MS }
  if (delta <= STAMP_LIVE_WINDOW_MS) return { source: 'live', stampedAtMs: null, skewClamped: false }
  if (delta <= STAMP_LATECOMER_MAX_MS) return { source: 'outbox_replay', stampedAtMs }
  return { source: 'correction', stampedAtMs }
}

// ===== Nachzügler-Sequenzvalidierung (R2/1.4): Einfügung an historischer Position, "Bestand gewinnt" =====
/** Zielstatus nach einem legalen Stempel (canStamp prüft die Vorbedingung). */
const STATUS_AFTER: Record<StampKind, AttStatus> = { kommen: 'an', gehen: 'aus', pause_start: 'pause', pause_ende: 'an' }
/** Der eingefügte Kandidat bekommt die höchste id → bei Sekunden-Gleichstand sortiert er ZULETZT (Bestand gewinnt). */
export const LATE_CANDIDATE_ID = Number.MAX_SAFE_INTEGER

export type LateInsertionResult = { ok: true } | { ok: false; reason: 'illegal_at_position' | 'breaks_suffix' }

/**
 * Deterministischer Skip-Fold über die nach (ts, id) geordnete Sequenz: jeder Stempel wird valid|invalid gelabelt.
 * Ein ILLEGALER Übergang wird ÜBERSPRUNGEN (Status unverändert), nicht angewandt — so ist auch eine Sequenz mit
 * Offline-Waisen (z. B. ein gehen, dessen kommen noch offline hängt) deterministisch bewertbar.
 */
function foldLabels(seq: readonly Stamp[]): Map<Stamp, boolean> {
  let status: AttStatus = 'aus'
  const labels = new Map<Stamp, boolean>()
  for (const s of sortByTs(seq)) {
    if (canStamp(status, s.kind)) {
      labels.set(s, true)
      status = STATUS_AFTER[s.kind]
    } else {
      labels.set(s, false)
    }
  }
  return labels
}

/**
 * Validiert die Einfügung eines backdatierten Stempels (Nachzügler) an seiner (ts, id)-Position. "Bestand gewinnt":
 * akzeptiert NUR, wenn (a) der Kandidat an seiner Position selbst legal ist UND (b) kein bislang gültiger
 * Bestandsstempel durch die Einfügung von gültig→ungültig kippt. Eine Waisen-Heilung (ungültig→gültig) ist ERLAUBT.
 * Sonst ablehnen → der Aufrufer erzeugt einen Auto-Korrekturantrag (nie den Bestand ändern — GoBD).
 */
export function validateLateInsertion(
  existing: readonly Stamp[],
  candidate: { kind: StampKind; ts: number },
): LateInsertionResult {
  const cand: Stamp = { kind: candidate.kind, ts: candidate.ts, id: LATE_CANDIDATE_ID }
  const before = foldLabels(existing)
  const after = foldLabels([...existing, cand])
  if (after.get(cand) === false) return { ok: false, reason: 'illegal_at_position' }
  for (const s of existing) {
    if (before.get(s) === true && after.get(s) === false) return { ok: false, reason: 'breaks_suffix' }
  }
  return { ok: true }
}

export interface Segment {
  from: number
  to: number
}
export interface WorkedSegments {
  /** Geschlossene Arbeitssegmente (Pausen sind ausgeschlossen, da pause_start sie beendet). */
  segments: Segment[]
  /** Offenes Segment (eingestempelt, noch kein Gegen-Stempel) — zählt NICHT als gearbeitet. */
  openSince: number | null
  /** Übersprungene Segmente (länger als maxSegmentSeconds = vermutlich vergessenes Ausstempeln). */
  anomalies: Segment[]
}

export interface WorkedOptions {
  maxSegmentSeconds?: number
}

/**
 * Paart die Stempel auf absoluten Zeitstempeln zu geschlossenen Arbeitssegmenten.
 * Öffnende Stempel: kommen, pause_ende. Schließende: gehen, pause_start.
 * Überlange Segmente (> maxSegmentSeconds) gelten als vergessenes Ausstempeln → Anomalie, nicht gezählt.
 */
export function workedSegments(stamps: readonly Stamp[], opts: WorkedOptions = {}): WorkedSegments {
  const maxMs = (opts.maxSegmentSeconds ?? DEFAULT_MAX_SEGMENT_SECONDS) * 1000
  const segments: Segment[] = []
  const anomalies: Segment[] = []
  let inAt: number | null = null
  for (const s of sortByTs(stamps)) {
    if (OPENING.has(s.kind)) {
      // nur öffnen, wenn nicht schon „an": ein 2. Kommen verwirft den ersten Start nicht
      if (inAt === null) inAt = s.ts
    } else if (CLOSING.has(s.kind) && inAt !== null) {
      const seg: Segment = { from: inAt, to: s.ts }
      if (s.ts - inAt > maxMs) anomalies.push(seg)
      else segments.push(seg)
      inAt = null
    }
  }
  return { segments, openSince: inAt, anomalies }
}

/** Gearbeitete Sekunden aus geschlossenen Segmenten — mitternachts-agnostisch (absolute ts). */
export function workedSeconds(
  stamps: readonly Stamp[],
  opts: WorkedOptions = {},
): { seconds: number; openSince: number | null; anomalies: Segment[] } {
  const { segments, openSince, anomalies } = workedSegments(stamps, opts)
  const ms = segments.reduce((sum, s) => sum + (s.to - s.from), 0)
  return { seconds: Math.max(0, Math.floor(ms / 1000)), openSince, anomalies }
}

// ===== ArbZG-Wächter (R4/1.8): Schicht-Aggregation + Verstoß-Erkennung (rein, I/O-frei) =====

export const ARBZG_MAX_WORK_SECONDS = 10 * 3600
export const ARBZG_BREAK6_WORK_SECONDS = 6 * 3600
export const ARBZG_BREAK9_WORK_SECONDS = 9 * 3600
export const ARBZG_BREAK_30_SECONDS = 30 * 60
export const ARBZG_BREAK_45_SECONDS = 45 * 60
export const ARBZG_MIN_REST_SECONDS = 11 * 3600
/** §4: eine Ruhepause ist in Zeitabschnitte von JE mind. 15 min teilbar — kürzere Stücke zählen NICHT als Pause. */
export const ARBZG_MIN_BREAK_CHUNK_SECONDS = 15 * 60
/** §4: „länger als 6 Stunden hintereinander … nicht ohne Ruhepause" — max. anrechenbare Arbeit am Stück ohne §4-Pause. */
export const ARBZG_MAX_WORK_STRETCH_SECONDS = 6 * 3600

/** Eine abgeschlossene Schicht (kommen … gehen) mit Netto-Arbeit + §4-Pausen. Nachtschicht-korrekt (absolute ts). */
export interface WorkShift {
  startMs: number
  endMs: number
  /** Netto-Arbeitssekunden (Summe der Arbeitssegmente, Pausen ausgeschlossen). */
  workedSeconds: number
  /** Summe der ANRECHENBAREN §4-Pausen: nur Pausen-Stücke von je ≥ 15 min (pause_start…pause_ende); kürzere Stücke
   *  zählen nicht, ebenso wenig eine End-Pause (Gehen aus Pause). */
  breakSeconds: number
  /** Längste ununterbrochene Arbeit (Summe der Arbeitssegmente ohne dazwischenliegende anrechenbare §4-Pause ≥15min);
   *  eine kürzere Unterbrechung setzt den „am Stück"-Zähler NICHT zurück (keine Ruhepause i. S. §4). */
  longestWorkStretchSeconds: number
  /** true, wenn ein Arbeitssegment > maxSegmentSeconds (vermutlich vergessenes Ausstempeln) → Zeiten unzuverlässig. */
  anomaly: boolean
}

/**
 * Gruppiert die Stempel zu abgeschlossenen SCHICHTEN (kommen aus→an … gehen →aus) — ArbZG denkt in Schichten,
 * nicht Kalendertagen (Nachtschicht über Mitternacht = EINE Schicht). Skip-Fold über die (ts,id)-Ordnung (wie
 * foldLabels): illegale Übergänge werden übersprungen (Status unverändert). Eine offene Schicht (ohne gehen) wird
 * NICHT zurückgegeben (nicht bewertbar). Eine END-Pause (pause_start…gehen) zählt weder als Arbeit noch als §4-Pause;
 * ebenso zählt ein Pausen-Stück unter 15 min nicht (§4-Mindeststück, breakSeconds führt nur anrechenbare Pausen).
 */
export function workShifts(stamps: readonly Stamp[], opts: WorkedOptions = {}): WorkShift[] {
  const maxMs = (opts.maxSegmentSeconds ?? DEFAULT_MAX_SEGMENT_SECONDS) * 1000
  const shifts: WorkShift[] = []
  let status: AttStatus = 'aus'
  let start = 0
  let workedMs = 0
  let breakMs = 0
  let workOpenAt = 0
  let pauseOpenAt = 0
  let stretchMs = 0 // Arbeit am Stück seit der letzten anrechenbaren §4-Pause (bzw. Schichtbeginn)
  let maxStretchMs = 0
  let anomaly = false
  for (const s of sortByTs(stamps)) {
    if (!canStamp(status, s.kind)) continue // illegalen Übergang überspringen (Skip-Fold, wie foldLabels)
    if (s.kind === 'kommen') {
      start = s.ts
      workedMs = 0
      breakMs = 0
      stretchMs = 0
      maxStretchMs = 0
      workOpenAt = s.ts
      anomaly = false
      status = 'an'
    } else if (s.kind === 'pause_start') {
      const seg = s.ts - workOpenAt
      if (seg > maxMs) anomaly = true
      workedMs += seg
      stretchMs += seg
      if (stretchMs > maxStretchMs) maxStretchMs = stretchMs
      pauseOpenAt = s.ts
      status = 'pause'
    } else if (s.kind === 'pause_ende') {
      // §4: nur Pausen-Stücke von je ≥ 15 min sind anrechenbar; kürzere Unterbrechungen zählen NICHT als Ruhepause.
      const chunk = s.ts - pauseOpenAt
      if (chunk >= ARBZG_MIN_BREAK_CHUNK_SECONDS * 1000) {
        breakMs += chunk
        stretchMs = 0 // anrechenbare Ruhepause setzt den „am Stück"-Zähler zurück; eine <15-min-Unterbrechung nicht
      }
      workOpenAt = s.ts
      status = 'an'
    } else {
      // gehen: aus 'an' → letztes Arbeitssegment schließen; aus 'pause' → End-Pause (weder Arbeit noch §4-Pause)
      if (status === 'an') {
        const seg = s.ts - workOpenAt
        if (seg > maxMs) anomaly = true
        workedMs += seg
        stretchMs += seg
        if (stretchMs > maxStretchMs) maxStretchMs = stretchMs
      }
      shifts.push({
        startMs: start,
        endMs: s.ts,
        workedSeconds: Math.max(0, Math.floor(workedMs / 1000)),
        breakSeconds: Math.max(0, Math.floor(breakMs / 1000)),
        longestWorkStretchSeconds: Math.max(0, Math.floor(maxStretchMs / 1000)),
        anomaly,
      })
      status = 'aus'
    }
  }
  return shifts
}

/** Alle Wächter-Flag-Typen als Laufzeit-Liste — EINE Wahrheit für Engine, Contract und die Ack-Whitelist des
 *  Handlers (eine handgepflegte Teilliste im Backend driftete bei jeder Flag-Erweiterung). */
export const ARBZG_FLAG_TYPES = [
  'over_10h',
  'break_short',
  'rest_short',
  'stretch_over_6h',
  // JArbSchG (unter 18, Phase-1/A2) — ersetzt je Schicht die Erwachsenen-Regeln durch die strengeren:
  'jugend_over_8h', // §8: max 8 h/Tag
  'jugend_break_short', // §11: 30 min ab >4,5 h, 60 min ab >6 h
  'jugend_stretch', // §11 Abs. 2: max 4,5 h am Stück ohne Ruhepause
  'jugend_rest_short', // §13: 12 h tägliche Freizeit
  'jugend_nachtarbeit', // §14: Beschäftigung nur 06–20 Uhr
  'jugend_schichtzeit_over_10h', // §12 i.V.m. §4 Abs.2: Schichtzeit (Arbeitszeit + Pausen) max 10 h
  // MuSchG (Schwangerschaft, Phase 2/3) — drittes Regime im selben Motor:
  'muschg_over_8_5h', // § 4 Abs. 1: max 8,5 h/Tag (Tagessumme)
  'muschg_nachtarbeit', // § 5: Nachtverbot 20–6 Uhr
  // JArbSchG-PERIODEN (Azubi-Block, Phase 3) — Wochen-/Monatsregeln, die der Schicht-Motor strukturell
  // nicht sehen kann. Eigener Motor: jarbschgPeriodViolations auf TimesheetDay[].
  'jugend_week_over_40h', // §8 Abs. 1: max 40 h/Woche
  'jugend_week_over_5_days', // §15 Satz 1: Fünf-Tage-Woche (Satz 2 ist nur SOLL → kein Flag)
  'jugend_samstage_im_monat', // §16 Abs. 2 Satz 2: zwei beschäftigungsfreie Samstage — SOLL, daher Hinweis
  'jugend_sonntag', // §17: Sonntagsruhe — für Kfz-Werkstätten gibt es KEINEN Ausnahmetatbestand
  'jugend_feiertag', // §18 i.V.m. §17 Abs. 2: Feiertagsruhe, gleiche Ausnahmeliste wie Sonntag
  // BBiG — hängt am AUSBILDUNGSVERHÄLTNIS, nicht am Alter (§ 15 BBiG gilt seit 2020 altersunabhängig):
  'azubi_schultag_beschaeftigt', // §15 Abs. 1 Satz 2 Nr. 2 BBiG: nach einem langen Schultag kein Betrieb
  'jugend_schultag_beschaeftigt', // §9 Abs. 1 Satz 2 Nr. 2 JArbSchG: derselbe Tatbestand, am ALTER haengend
] as const

// ---- Abwesenheiten (Phase 2, Block 1): Wirkung als Daten — docs/DESIGN-zeit-abwesenheiten.md ----

/** Zeitkonto-Wirkung eines Abwesenheits-Typs. Laufzeit-Liste = EINE Wahrheit für Engine + Server-Validierung. */
export const ABSENCE_EFFECTS = ['gutschrift', 'soll_null', 'saldo_abbau'] as const
export type AbsenceEffect = (typeof ABSENCE_EFFECTS)[number]
export const isAbsenceEffect = (v: unknown): v is AbsenceEffect => typeof v === 'string' && (ABSENCE_EFFECTS as readonly string[]).includes(v)

/**
 * ZWEITE Dimension neben dem effect (Azubi-Block): 'add' verrechnet die Gutschrift ZUSÄTZLICH zur gestempelten
 * Zeit, 'fill' füllt den Tag nur bis zum Soll auf. Bestand und Default sind 'add' — die ausführliche
 * Begründung steht bei timesheetDays(). ⚠️ Für die Kollisionsgruppe 'ausbildung' ist 'fill' verboten: es wäre
 * genau der Deckel, der reale Arbeitszeit löscht (§ 16 ArbZG).
 */
export const STAMP_BEHAVIORS = ['add', 'fill'] as const
export type StampBehavior = (typeof STAMP_BEHAVIORS)[number]
export const isStampBehavior = (v: unknown): v is StampBehavior => typeof v === 'string' && (STAMP_BEHAVIORS as readonly string[]).includes(v)

/**
 * ERFASSUNGS-Dimension: was schließt sich am selben Tag gegenseitig aus? Bewusst getrennt von stampBehavior —
 * sonst müsste man die RECHNUNG ändern (Urlaub auf 'fill'), um eine KOLLISIONS-Regel zu drehen.
 * 'ausbildung' = Berufsschulzeit i. S. d. § 9 Abs. 2 JArbSchG / § 15 Abs. 2 BBiG (zählt als Arbeitszeit,
 * wird mit der durchschnittlichen täglichen Arbeitszeit angerechnet).
 */
export const COLLISION_GROUPS = ['abwesend', 'ausbildung'] as const
export type CollisionGroup = (typeof COLLISION_GROUPS)[number]
export const isCollisionGroup = (v: unknown): v is CollisionGroup => typeof v === 'string' && (COLLISION_GROUPS as readonly string[]).includes(v)

export interface AbsenceDayEffect {
  effect: AbsenceEffect
  /** 1 = ganzer Tag, 0.5 = halber Tag. Bei mehreren Wirkungen: der Faktor DIESER (der stärksten) Wirkung. */
  factor: number
  stampBehavior: StampBehavior
  collisionGroup: CollisionGroup
  /**
   * Summe der Faktoren ALLER Wirkungen dieses Tages (über Kollisionsgruppen hinweg). ≤ 1 heißt: die Wirkungen
   * ergänzen sich widerspruchsfrei (halber Schultag + halber Krank-Tag = ein voller Tag). > 1 heißt: echter
   * ERFASSUNGSwiderspruch (ganzer Urlaubstag + ganzer Schultag) — wer im Urlaub war, war nicht in der Schule.
   */
  factorSum: number
  /** Weitere Wirkungen desselben Tages, stärkste zuerst — leer im Normalfall. */
  others: AbsenceDayEffect[]
}

export interface AbsenceLike {
  fromDay: string
  toDay: string
  halfDay?: number | boolean | null
  effect: string
  status: string
  /** Zeilen-Snapshots (Azubi-Block); fehlend → Bestands-Default. */
  stampBehavior?: string | null
  collisionGroup?: string | null
}

/**
 * Baut die Tag→Wirkung-Karte aus GENEHMIGTEN Abwesenheiten, geclippt auf [from, to]. Unbekannte effects
 * werden ignoriert (defensive Vorwärts-Kompatibilität).
 *
 * MEHRERE WIRKUNGEN AN EINEM TAG sind seit dem Azubi-Block möglich: `overlapExists` prüft nur noch INNERHALB
 * einer Kollisionsgruppe, damit ein ganzjähriger Berufsschul-Serieneintrag nicht jeden Urlaubs- und
 * Krankantrag des Azubis blockiert. Die Karte liefert weiterhin genau EINEN Eintrag je Tag (alle Aufrufer
 * bleiben unverändert), trägt die Nebenwirkungen aber in `others` und die Faktor-Summe in `factorSum`.
 *
 * Die stärkste Wirkung (größter Faktor; bei Gleichstand gewinnt 'abwesend' vor 'ausbildung') führt. Die
 * Aufrufer-Reihenfolge ist damit nur noch Tiebreaker — trotzdem sortiert der Aufrufer deterministisch
 * (ORDER BY from_day, id), weil das Ergebnis per Hard-Close eingefroren wird.
 */
export function absenceDayMap(absences: readonly AbsenceLike[], from: string, to: string): Map<string, AbsenceDayEffect> {
  const map = new Map<string, AbsenceDayEffect>()
  for (const a of absences) {
    if (a.status !== 'genehmigt' || !isAbsenceEffect(a.effect)) continue
    const start = a.fromDay > from ? a.fromDay : from
    const end = a.toDay < to ? a.toDay : to
    if (start > end) continue
    const startMs = Date.parse(start + 'T00:00:00Z')
    const endMs = Date.parse(end + 'T00:00:00Z')
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) continue
    const hit: AbsenceDayEffect = {
      effect: a.effect,
      factor: a.halfDay ? 0.5 : 1,
      stampBehavior: isStampBehavior(a.stampBehavior) ? a.stampBehavior : 'add',
      collisionGroup: isCollisionGroup(a.collisionGroup) ? a.collisionGroup : 'abwesend',
      factorSum: 0,
      others: [],
    }
    for (let d = startMs; d <= endMs; d += DAY_MS) {
      const dayKey = utcDayKey(d)
      const prev = map.get(dayKey)
      if (!prev) {
        map.set(dayKey, { ...hit, factorSum: hit.factor })
        continue
      }
      // Stärkere Wirkung führt; 'abwesend' schlägt 'ausbildung' bei gleichem Faktor (Urlaub/Krank ist die
      // konkretere Aussage über den Tag — der Schultag ist dann der, der ausgefallen ist).
      const takesLead = hit.factor > prev.factor || (hit.factor === prev.factor && prev.collisionGroup === 'ausbildung' && hit.collisionGroup === 'abwesend')
      const sum = prev.factorSum + hit.factor
      if (takesLead) {
        map.set(dayKey, { ...hit, factorSum: sum, others: [{ ...prev, factorSum: 0, others: [] }, ...prev.others] })
      } else {
        map.set(dayKey, { ...prev, factorSum: sum, others: [...prev.others, { ...hit, factorSum: 0, others: [] }] })
      }
    }
  }
  return map
}

export interface SeriesRange {
  from: string
  to: string
}

/**
 * Die Kalendertage einer Wochentags-SERIE über [from, to], abzüglich der Ausnahme-Zeiträume (Ferien,
 * Praktikumsblöcke). Rein rechnend, ohne I/O, aufsteigend sortiert und duplikatfrei.
 *
 * Wochentage sind 0=Mo … 6=So — identisch zur Dienstplan-Konvention `(getUTCDay() + 6) % 7`, damit „Dienstag
 * ist Schultag" im Dienstplan und in der Serie dieselbe Zahl bedeutet. Kaputte Datumsangaben liefern eine
 * leere Liste (nie eine Endlosschleife); der Aufrufer deckelt Spanne und Zeilenzahl zusätzlich.
 */
export function expandSeriesDays(
  from: string,
  to: string,
  weekdays: readonly number[],
  exceptions: readonly SeriesRange[] = [],
): string[] {
  const fromMs = Date.parse(from + 'T00:00:00Z')
  const toMs = Date.parse(to + 'T00:00:00Z')
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs < fromMs) return []
  const wanted = new Set(weekdays.filter((w) => Number.isInteger(w) && w >= 0 && w <= 6))
  if (wanted.size === 0) return []
  const blocked = exceptions.filter((e) => typeof e?.from === 'string' && typeof e?.to === 'string' && e.from <= e.to)
  const out: string[] = []
  for (let d = fromMs; d <= toMs; d += DAY_MS) {
    if (!wanted.has((new Date(d).getUTCDay() + 6) % 7)) continue
    const dayKey = utcDayKey(d)
    if (blocked.some((e) => dayKey >= e.from && dayKey <= e.to)) continue
    out.push(dayKey)
  }
  return out
}

/**
 * Anrechenbare Gutschrift-Faktoren eines Tages, aufgeteilt nach Kollisionsgruppe — die Grundlage der
 * Tages-Gutschrift. Solange die Faktor-Summe ≤ 1 bleibt, wirken ALLE Gutschriften nebeneinander (halber
 * Schultag + halber Krank-Tag = ein voller entschuldigter Tag). Erst darüber gilt nur noch die führende
 * Wirkung — dann liegt ein Erfassungswiderspruch vor, der gemeldet und nicht verrechnet wird.
 */
function creditFactors(abs: AbsenceDayEffect | undefined): { ausbildung: number; sonstige: number } {
  const out = { ausbildung: 0, sonstige: 0 }
  if (!abs) return out
  const wirkungen = abs.factorSum > 1 ? [abs] : [abs, ...abs.others]
  for (const w of wirkungen) {
    if (w.effect !== 'gutschrift') continue
    if (w.collisionGroup === 'ausbildung') out.ausbildung += w.factor
    else out.sonstige += w.factor
  }
  return out
}

/**
 * Anteil eines Tages, der für den § 3-Durchschnitt NEUTRAL zu stellen ist (docs/DESIGN-zeit-arbzg-durchschnitt.md 2.2).
 *
 * Neutral heißt: der Tag fällt aus Zähler UND Nenner. Begründung: Urlaub und Krankheit sind eigenständige
 * Ansprüche — sie dürfen keine Mehrarbeit abarbeiten. Rechnete man sie als Werktag mit null Stunden mit,
 * glichen vier Wochen à 60 h durch eine Urlaubswoche exakt aus (240 h / 240 h = 100 %); der Urlaub des
 * Mitarbeiters hätte die rechtswidrigen Überstunden getilgt. Neutral gestellt bleibt der Verstoß sichtbar
 * (240 h / 192 h = 125 %).
 *
 * ⚠️ 'saldo_abbau' (Freizeitausgleich) ist bewusst NICHT dabei — und das ist die Zeile, an der der ganze
 * Wächter sonst zerbräche. FZA IST die verkürzte Arbeitszeit, die § 3 Satz 2 als Ausgleich verlangt.
 * Neutralisierte man ihn, verschwände mit jedem FZA-Tag auch dessen ausgleichende Wirkung — der Betrieb
 * könnte den Verstoß dann strukturell NIE beheben, egal wie viel Freizeit er gewährt. Ein Wächter mit
 * unerreichbarem Grün ist so wertlos wie einer, der nie ausschlägt.
 *
 * ⚠️ Gruppe 'ausbildung' ebenfalls nicht: Berufsschulzeit zählt als Arbeitszeit in den ZÄHLER (2.3), der
 * Tag bleibt ein ganz normaler Werktag.
 */
function absenceNeutralFactor(abs: AbsenceDayEffect | undefined): number {
  if (!abs) return 0
  const wirkungen = abs.factorSum > 1 ? [abs] : [abs, ...abs.others]
  let sum = 0
  for (const w of wirkungen) {
    if (w.collisionGroup === 'ausbildung') continue
    if (w.effect === 'gutschrift' || w.effect === 'soll_null') sum += w.factor
  }
  // Gedeckelt: mehr als ein ganzer Tag kann nie neutral gestellt werden, sonst entstünde ein negativer
  // Nenner-Beitrag (halber Urlaub + halber unbezahlter Tag + halber Krank-Tag = 1,5).
  return Math.min(1, sum)
}

// ---- Urlaubskonto (Phase 2/2a, BUrlG) — docs/DESIGN-zeit-urlaubskonto.md ----
// Alles in ZEHNTELtagen als Integer (nie Float); halber Tag = 5.

/**
 * § 5-Zwölftelung: bei Eintritt im laufenden Jahr zählt jeder VOLLE Beschäftigungsmonat 1/12 des
 * Jahresanspruchs; Bruchteile ≥ 0,5 Tage werden auf volle Tage AUFgerundet (§ 5 Abs. 2 — kleinere
 * Bruchteile bleiben stehen, werden NIE abgerundet). Ohne/mit kaputtem oder früherem Eintritt: voller
 * Anspruch (sicherer, arbeitnehmerfreundlicher Default); Eintritt in einem SPÄTEREN Jahr: 0.
 */
export function proratedEntitlementTenths(entitlementTenths: number, entryDate: string | null | undefined, year: number): number {
  if (!entryDate || !/^\d{4}-\d{2}-\d{2}$/.test(entryDate)) return entitlementTenths
  const entryYear = Number(entryDate.slice(0, 4))
  if (entryYear < year) return entitlementTenths
  if (entryYear > year) return 0
  // § 4-Wartezeit (Review-HIGH): wer bis EINSCHLIESSLICH 01.07. eintritt, erfüllt die 6-Monats-Wartezeit
  // noch im Kalenderjahr → VOLLER Jahresanspruch. Gezwölftelt wird nur, wenn die Wartezeit im Jahr nicht
  // mehr erfüllbar ist (§ 5 Abs. 1 lit. a BUrlG) — sonst zeigte die App still zu wenig gesetzlichen Urlaub.
  if (entryDate <= `${year}-07-01`) return entitlementTenths
  const entryMonth = Number(entryDate.slice(5, 7)) // 1..12
  const fullMonths = 12 - entryMonth + (entryDate.slice(8, 10) === '01' ? 1 : 0)
  if (fullMonths <= 0) return 0
  // Aufrundungs-Entscheidung auf dem EXAKTEN Produkt (Review-Fund Doppel-Rundung: erst auf Zehntel runden
  // hob Bruchteile ab 0,45 Tagen fälschlich auf den vollen Tag). raw zählt in Zwölftel-Zehnteln: 1 Tag = 120.
  const raw = entitlementTenths * fullMonths
  const rem = raw % 120
  return rem >= 60 ? (Math.floor(raw / 120) + 1) * 10 : Math.round(raw / 12)
}

/**
 * Verbrauchte Urlaubstage (Zehntel) im Kalenderjahr: nur GENEHMIGTE Abwesenheiten mit typeKey 'urlaub';
 * ein Tag verbraucht nur, wenn er laut wirksamem Wochenmuster ein Solltag ist und KEIN Feiertag —
 * dieselbe Tages-Wahrheit wie die Zeitkonto-Gutschrift (nie Werk-/Kalendertage mischen).
 */
export function vacationConsumedTenths(
  absences: ReadonlyArray<AbsenceLike & { typeKey: string }>,
  plans: readonly WorkPlanDay[],
  holidayDays: ReadonlySet<string>,
  year: number,
): number {
  const from = `${year}-01-01`
  const to = `${year}-12-31`
  let tenths = 0
  for (const a of absences) {
    if (a.status !== 'genehmigt' || a.typeKey !== 'urlaub') continue
    const start = a.fromDay > from ? a.fromDay : from
    const end = a.toDay < to ? a.toDay : to
    if (start > end) continue
    const startMs = Date.parse(start + 'T00:00:00Z')
    const endMs = Date.parse(end + 'T00:00:00Z')
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) continue
    for (let d = startMs; d <= endMs; d += DAY_MS) {
      const dayKey = utcDayKey(d)
      if (holidayDays.has(dayKey)) continue
      if (sollSecondsForDay(plans, dayKey) <= 0) continue
      tenths += a.halfDay ? 5 : 10
    }
  }
  return tenths
}

export interface VacationAccount {
  year: number
  /** Jahresanspruch nach § 5-Zwölftelung (Zehntel). */
  entitlementTenths: number
  /** Übertrag aus dem Vorjahr (Zehntel, gepflegtes Feld). */
  carryTenths: number
  consumedTenths: number
  /** entitlement + carry − consumed; darf negativ werden (Anzeige warnt, blockt nicht). */
  remainingTenths: number
  /** true, wenn gezwölftelt wurde (Eintritt im laufenden Jahr) — die UI erklärt das. */
  prorated: boolean
}

export function vacationAccount(
  year: number,
  user: { vacationDaysTenths: number; vacationCarryTenths: number; entryDate: string | null },
  absences: ReadonlyArray<AbsenceLike & { typeKey: string }>,
  plans: readonly WorkPlanDay[],
  holidayDays: ReadonlySet<string>,
): VacationAccount {
  const entitlementTenths = proratedEntitlementTenths(user.vacationDaysTenths, user.entryDate, year)
  const consumedTenths = vacationConsumedTenths(absences, plans, holidayDays, year)
  return {
    year,
    entitlementTenths,
    carryTenths: user.vacationCarryTenths,
    consumedTenths,
    remainingTenths: entitlementTenths + user.vacationCarryTenths - consumedTenths,
    prorated: entitlementTenths !== user.vacationDaysTenths,
  }
}

// ---- Timer-Anwesenheits-Gate (A3): Deckung einer Auftragszeit-Buchung durch gestempelte Arbeit ----

/** Toleranz der Deckungsprüfung (Uhren-/Tipp-Slack) — erst darüber gilt Auftragszeit als „ohne Anwesenheit". */
export const JOB_ATT_TOLERANCE_SECONDS = 5 * 60

export interface WorkSegment {
  startMs: number
  endMs: number
}

/**
 * Reine ARBEITS-Segmente (Status 'an') aus einer Stempel-Liste: kommen/pause_ende öffnet, pause_start/gehen
 * schließt. Pausen decken keine Auftragszeit. Eine am Ende noch OFFENE Schicht (eingestempelt) deckt bis
 * `nowMs` — der Normalfall, denn die Buchung kommt beim Timer-Stopp mitten in der Schicht. Reihenfolge-robust,
 * Segmente überlappungsfrei (Zustandsmaschine). Jedes Segment (offen wie geschlossen) deckt höchstens
 * 16 h (DEFAULT_MAX_SEGMENT_SECONDS) — konsistent zur Anomalie-Definition des Systems: eine vergessene
 * Ausstempelung darf nicht unbegrenzt Auftragszeit decken und so die Divergenz-Meldung stummschalten.
 */
export function workSegments(stamps: readonly Stamp[], nowMs: number): WorkSegment[] {
  const capMs = DEFAULT_MAX_SEGMENT_SECONDS * 1000
  const sorted = [...stamps].sort((a, b) => a.ts - b.ts)
  const segs: WorkSegment[] = []
  let openSince: number | null = null
  for (const s of sorted) {
    if (s.kind === 'kommen' || s.kind === 'pause_ende') {
      if (openSince == null) openSince = s.ts
    } else {
      if (openSince != null && s.ts > openSince) segs.push({ startMs: openSince, endMs: Math.min(s.ts, openSince + capMs) })
      openSince = null
    }
  }
  if (openSince != null && nowMs > openSince) segs.push({ startMs: openSince, endMs: Math.min(nowMs, openSince + capMs) })
  return segs
}

/** Sekunden des Intervalls [startMs, endMs], die NICHT durch Arbeitssegmente gedeckt sind (≥ 0, abgerundet). */
export function uncoveredJobSeconds(startMs: number, endMs: number, segments: readonly WorkSegment[]): number {
  if (endMs <= startMs) return 0
  let coveredMs = 0
  for (const seg of segments) {
    coveredMs += Math.max(0, Math.min(endMs, seg.endMs) - Math.max(startMs, seg.startMs))
  }
  return Math.max(0, Math.floor((endMs - startMs - coveredMs) / 1000))
}

export type ArbZgFlagType = (typeof ARBZG_FLAG_TYPES)[number]

// ---- JArbSchG-Grenzen (Jugendarbeitsschutz, Beschäftigte unter 18) ----
export const JARBSCHG_MAX_WORK_SECONDS = 8 * 3600 // §8 Abs. 1
export const JARBSCHG_BREAK45_WORK_SECONDS = Math.round(4.5 * 3600) // §11: >4,5 h → 30 min
export const JARBSCHG_BREAK6_WORK_SECONDS = 6 * 3600 // §11: >6 h → 60 min
export const JARBSCHG_BREAK_30_SECONDS = 30 * 60
export const JARBSCHG_BREAK_60_SECONDS = 60 * 60
export const JARBSCHG_MIN_REST_SECONDS = 12 * 3600 // §13 tägliche Freizeit
export const JARBSCHG_MAX_STRETCH_SECONDS = Math.round(4.5 * 3600) // §11 Abs. 2
export const JARBSCHG_DAY_START_MIN = 6 * 60 // §14: Beschäftigung frühestens 06:00 …
export const JARBSCHG_DAY_END_MIN = 20 * 60 // … spätestens bis 20:00
/** § 12 i. V. m. § 4 Abs. 2: SCHICHTZEIT = tägliche Arbeitszeit + Ruhepausen, höchstens 10 h. Die
 *  11-h-Ausnahmen gelten nur für Gaststätten/Landwirtschaft/Tierhaltung/Bau- und Montagestellen — für eine
 *  stationäre Kfz-Werkstatt bleibt es bei 10 h. */
export const JARBSCHG_MAX_SHIFT_SPAN_SECONDS = 10 * 3600
/** § 8 Abs. 2a JArbSchG: an den übrigen Werktagen DERSELBEN Woche sind 8,5 h zulässig, wenn an einzelnen
 *  Werktagen die Arbeitszeit auf unter 8 h verkürzt ist.
 *  ⚠️ NICHT Abs. 2 — der regelt die Brückentag-/Feiertagsverteilung über fünf zusammenhängende Wochen.
 *  Beide Absätze nennen „achteinhalb Stunden", daher die Verwechslungsgefahr (gegen den amtlichen Wortlaut
 *  geprüft). */
export const JARBSCHG_MAX_WORK_SECONDS_AUSGLEICH = Math.round(8.5 * 3600)

// ---- Lohn-Export Stufe A: Zeit-Formate fürs Lohnbüro (Semikolon-CSV, Dezimal-KOMMA) ----

/** Minuten → Industriestunden mit Dezimal-Komma ('9615' → '160,25'); negativ mit Minus. */
export function minutesToIndustrial(min: number): string {
  return (min / 60).toFixed(2).replace('.', ',')
}

/** Minuten → 'HH:MM' (auch > 24 h, z. B. '160:15'); negativ als '-12:30'. */
export function minutesToHHMM(min: number): string {
  const sign = min < 0 ? '-' : ''
  const abs = Math.abs(min)
  return `${sign}${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, '0')}`
}

// ---- Minijob-Grenze (§ 8 SGB IV; Jahres-Parameter statt Hardcode — 2027er Werte stehen schon fest) ----
export const MINIJOB_PARAMS: ReadonlyArray<{ year: number; grenzeCents: number; mindestlohnCents: number }> = [
  { year: 2026, grenzeCents: 60_300, mindestlohnCents: 1_390 },
  { year: 2027, grenzeCents: 63_300, mindestlohnCents: 1_460 },
]

/** Monatliche Stunden-Obergrenze eines Minijobs in MINUTEN; ohne gepflegten Lohn gilt der Mindestlohn
 *  (= größtmögliche Stundenzahl — der Warntext nennt die Rechenbasis). Unbekanntes Jahr: jüngster Eintrag. */
export function minijobLimitMinutes(year: number, stundenlohnCents?: number | null): number {
  const param = MINIJOB_PARAMS.filter((p) => p.year <= year).at(-1) ?? MINIJOB_PARAMS[0]!
  const lohn = stundenlohnCents && stundenlohnCents > 0 ? stundenlohnCents : param.mindestlohnCents
  return Math.floor((param.grenzeCents / lohn) * 60)
}

// ---- MuSchG-Grenzen (Schwangerschaft; §§ 4/5 MuSchG, strafbewehrt) ----
export const MUSCHG_MAX_WORK_SECONDS = Math.round(8.5 * 3600) // § 4 Abs. 1: 8,5 h/Tag
export const MUSCHG_WARN_SECONDS = 8 * 3600 // Nudge-Schwelle vor der Grenze

/** Alter (volle Jahre) am Kalendertag `dayKey` — reine String-Arithmetik auf 'YYYY-MM-DD'. */
export function ageOnDay(birthdate: string, dayKey: string): number {
  const years = Number(dayKey.slice(0, 4)) - Number(birthdate.slice(0, 4))
  return dayKey.slice(5) < birthdate.slice(5) ? years - 1 : years
}

/** Minderjährig (<18, JArbSchG) am Kalendertag? Ohne/mit kaputtem Geburtsdatum → false (Erwachsenen-Regeln). */
export function isMinorOn(birthdate: string | null | undefined, dayKey: string): boolean {
  if (!birthdate || !/^\d{4}-\d{2}-\d{2}$/.test(birthdate)) return false
  return ageOnDay(birthdate, dayKey) < 18
}

/**
 * Tag, ab dem das ArbZG für diese Person gilt — der 18. Geburtstag. Davor gilt nach § 18 Abs. 2 ArbZG das
 * JArbSchG ANSTELLE des ArbZG, die Zeiten gehören also nicht in den § 3-Durchschnitt.
 * Ohne/mit kaputtem Geburtsdatum → null (kein Klemmen; der Erwachsenen-Default gilt ohnehin).
 * Der 29.02. wird auf den 01.03. gelegt: in einem Nicht-Schaltjahr gäbe es den Geburtstag sonst nicht.
 */
export function arbzgEligibleFrom(birthdate: string | null | undefined): string | null {
  if (!birthdate || !/^\d{4}-\d{2}-\d{2}$/.test(birthdate)) return null
  const jahr = Number(birthdate.slice(0, 4)) + 18
  const rest = birthdate.slice(5)
  if (rest === '02-29') return `${jahr}-03-01`
  return `${jahr}-${rest}`
}

/** Ein ArbZG-Verstoß, verankert an einer Schicht (start/end als epoch ms). Kontextzahlen je nach Typ gesetzt. */
export interface ArbZgFlag {
  type: ArbZgFlagType
  shiftStartMs: number
  shiftEndMs: number
  /** over_10h: Ist-Arbeitssekunden. */
  workedSeconds?: number
  /** break_short: geforderte / tatsächliche Pausensekunden. */
  requiredBreakSeconds?: number
  actualBreakSeconds?: number
  /** rest_short: Ruhesekunden zwischen der Vorschicht und dieser Schicht. */
  restSeconds?: number
  /** stretch_over_6h: längste Arbeit am Stück (Sekunden) ohne anrechenbare §4-Pause. */
  stretchSeconds?: number
  /** Schicht mit unplausibel langem Segment (vermutlich vergessenes Ausstempeln) → Hinweis statt Alarm. */
  anomaly?: boolean
  /** Perioden-Flags (Azubi-Block): Woche/Monat/Tag statt Schicht. shiftStartMs/EndMs tragen dann die
   *  Perioden-Grenzen, damit Ack-Anker und Zeitraum-Filter unverändert funktionieren. */
  periodKind?: 'day' | 'week' | 'month'
  /** 'YYYY-MM-DD' (Tag bzw. Montags-Datum der Woche) oder 'YYYY-MM'. */
  periodKey?: string
  /** Beschäftigungstage bzw. beschäftigte Samstage — je nach Typ. */
  count?: number
  /** Die Periode lag nicht vollständig im Rechenbereich → Hinweis statt Alarm. */
  partial?: boolean
  /** SOLL-Vorschrift (§ 16 Abs. 2 Satz 2): Hinweis, kein Verstoß — nicht bußgeldbewehrt. */
  advisory?: boolean
  /** R4/1.10: vom Chef quittiert (gesehen/bearbeitet) — der Server setzt es aus arbzg_acks; die pure Engine nie. */
  acked?: boolean
  ackedBy?: number
  ackedAt?: string
}

/** §4-Soll-Pause für eine gegebene Netto-Arbeitszeit (Sekunden): >9h→45min, >6h→30min, sonst 0. */
export function requiredBreakSeconds(workedSecondsOfShift: number): number {
  if (workedSecondsOfShift > ARBZG_BREAK9_WORK_SECONDS) return ARBZG_BREAK_45_SECONDS
  if (workedSecondsOfShift > ARBZG_BREAK6_WORK_SECONDS) return ARBZG_BREAK_30_SECONDS
  return 0
}

/** §11-JArbSchG-Soll-Pause (unter 18): >6h→60min, >4,5h→30min, sonst 0. */
export function requiredBreakSecondsJugend(workedSecondsOfShift: number): number {
  if (workedSecondsOfShift > JARBSCHG_BREAK6_WORK_SECONDS) return JARBSCHG_BREAK_60_SECONDS
  if (workedSecondsOfShift > JARBSCHG_BREAK45_WORK_SECONDS) return JARBSCHG_BREAK_30_SECONDS
  return 0
}

/** Lokale Minuten seit Mitternacht (Wanduhr) eines Zeitpunkts — für das §14-Beschäftigungsfenster 06–20 Uhr. */
function localMinutesOfDay(epochMs: number, timeZone: string = DEFAULT_TIME_ZONE): number {
  const hm = formatWallClock(epochMs, timeZone)
  return Number(hm.slice(0, 2)) * 60 + Number(hm.slice(3, 5))
}

/**
 * Erkennt Arbeitszeit-Verstöße über eine Schicht-Liste (aus workShifts). ERWACHSENE (ArbZG): je Schicht §3
 * (>10h) und §4 (Pause zu kurz UND >6h am Stück), je Schicht-Paar §5 (Ruhezeit <11h, auf der späteren Schicht
 * markiert). UNTER 18 (JArbSchG, Phase-1/A2 — `opts.birthdate` gesetzt und am SCHICHTTAG minderjährig, wichtig
 * für den 18. Geburtstag mitten im Zeitraum): die strengeren Jugend-Regeln ERSETZEN die Erwachsenen-Checks je
 * Schicht — §8 >8h, §11 Pausen 30/60min + max 4,5h am Stück, §13 Freizeit 12h, §14 Beschäftigung nur 06–20 Uhr.
 * Ruhezeit-Prüfung wird nach einer Anomalie-Schicht übersprungen (deren endMs ist unzuverlässig). anomaly wird
 * durchgereicht → die UI trennt einen echten Verstoß von „vermutlich Ausstempeln vergessen". Reihenfolge-robust.
 */
export function arbzgViolations(
  shifts: readonly WorkShift[],
  opts: {
    birthdate?: string | null
    mutterschutz?: boolean
    muschgVon?: string | null
    muschgBis?: string | null
    /** § 8 Abs. 2a JArbSchG: Tage, an denen 8,5 h zulässig sind, weil die Woche einen verkürzten Werktag
     *  hat (siehe ausgleichsTageJArbSchG). Der Schicht-Motor kann das nicht selbst wissen — ohne diesen
     *  Satz bleibt es bei der strengen 8-h-Grenze, also beim bisherigen, konservativen Verhalten. */
    ausgleichsTage?: ReadonlySet<string>
  } = {},
): ArbZgFlag[] {
  // MuSchG-Anker (Review-Fix): mit gesetzter Spanne zählt der SCHICHTTAG (keine rückwirkenden Falsch-
  // Verstöße; Historie überlebt das Abhaken). Ohne Spanne: Momentzustand (Tests/Einfachnutzung).
  const schwangerAt = (day: string): boolean =>
    opts.muschgVon != null ? day >= opts.muschgVon && (opts.muschgBis == null || day <= opts.muschgBis) : opts.mutterschutz === true
  const flags: ArbZgFlag[] = []
  const sorted = [...shifts].sort((a, b) => a.startMs - b.startMs)
  // §8 JArbSchG deckelt die TAGES-Arbeitszeit: ein Mittags-Split via gehen+kommen ist EINE tägliche Arbeitszeit
  // (siehe Ruhezeit-Kommentar unten) — zwei 4,5-h-Schichten am selben Tag (9 h) dürfen die 8-h-Grenze nicht
  // flaglos umgehen. Daher Tagessumme je lokalem Kalendertag; das Flag ankert an der LETZTEN Schicht des Tages.
  const dayWorked = new Map<string, { total: number; anomaly: boolean }>()
  for (const sh of sorted) {
    const k = localDayKey(sh.startMs)
    const d = dayWorked.get(k) ?? { total: 0, anomaly: false }
    d.total += sh.workedSeconds
    d.anomaly = d.anomaly || !!sh.anomaly
    dayWorked.set(k, d)
  }
  for (let i = 0; i < sorted.length; i++) {
    const sh = sorted[i]!
    const dayKey = localDayKey(sh.startMs)
    const minor = isMinorOn(opts.birthdate, dayKey)
    // Regime-Vorrang: Minderjährige laufen unter JArbSchG (überall strenger oder gleich als MuSchG —
    // 8 h < 8,5 h, Nachtfenster identisch); sonst greift bei Schwangerschaft das MuSchG-Regime.
    const schwanger = !minor && schwangerAt(dayKey)
    if (minor || schwanger) {
      const next = sorted[i + 1]
      const day = dayWorked.get(dayKey)!
      // § 8 Abs. 2a: an einem Tag, dessen Woche einen verkürzten Werktag (< 8 h) enthält, sind 8,5 h
      // zulässig. Über 8,5 h bleibt es immer ein Verstoß.
      const dayMax = minor
        ? opts.ausgleichsTage?.has(dayKey)
          ? JARBSCHG_MAX_WORK_SECONDS_AUSGLEICH
          : JARBSCHG_MAX_WORK_SECONDS
        : MUSCHG_MAX_WORK_SECONDS
      if ((!next || localDayKey(next.startMs) !== dayKey) && day.total > dayMax) {
        flags.push({ type: minor ? 'jugend_over_8h' : 'muschg_over_8_5h', shiftStartMs: sh.startMs, shiftEndMs: sh.endMs, workedSeconds: day.total, anomaly: day.anomaly })
      }
    } else if (sh.workedSeconds > ARBZG_MAX_WORK_SECONDS) {
      flags.push({ type: 'over_10h', shiftStartMs: sh.startMs, shiftEndMs: sh.endMs, workedSeconds: sh.workedSeconds, anomaly: sh.anomaly })
    }
    const req = minor ? requiredBreakSecondsJugend(sh.workedSeconds) : requiredBreakSeconds(sh.workedSeconds)
    if (req > 0 && sh.breakSeconds < req) {
      flags.push({
        type: minor ? 'jugend_break_short' : 'break_short',
        shiftStartMs: sh.startMs,
        shiftEndMs: sh.endMs,
        requiredBreakSeconds: req,
        actualBreakSeconds: sh.breakSeconds,
        anomaly: sh.anomaly,
      })
    }
    // Nicht länger als 6 h (§4 ArbZG) bzw. 4,5 h (§11 Abs. 2 JArbSchG) am Stück ohne anrechenbare Ruhepause.
    // Eigenständig zum break_short (Gesamt-Pause): 6h30m durchgehend + danach 30min Pause erfüllt die
    // Gesamt-Pause, verstößt aber gegen die Am-Stück-Regel.
    const maxStretch = minor ? JARBSCHG_MAX_STRETCH_SECONDS : ARBZG_MAX_WORK_STRETCH_SECONDS
    if (sh.longestWorkStretchSeconds > maxStretch) {
      flags.push({
        type: minor ? 'jugend_stretch' : 'stretch_over_6h',
        shiftStartMs: sh.startMs,
        shiftEndMs: sh.endMs,
        stretchSeconds: sh.longestWorkStretchSeconds,
        anomaly: sh.anomaly,
      })
    }
    // §12 JArbSchG i. V. m. §4 Abs. 2: die SCHICHTZEIT (Arbeitszeit + Ruhepausen) darf 10 h nicht
    // überschreiten. Eigenständig neben §8: wer 07:00–18:00 arbeitet und dazwischen 3 h Pause macht, hält
    // §8 (8 h Arbeit) und §11 (Pause) ein und reißt trotzdem §12. Die 11-h-Ausnahmen gelten nicht für
    // stationäre Kfz-Werkstätten.
    // ⚠️ Gemessen wird über den ganzen TAG, nicht je Schicht: ein Mittags-Ausstempeln (gehen + kommen)
    // erzeugt zwei Schichten und würde die Grenze sonst vollständig umgehen — § 4 Abs. 2 stellt aber auf
    // die tägliche Arbeitszeit zuzüglich der Ruhepausen ab, also auf die Spanne vom ersten Beginn bis zum
    // letzten Ende des Tages. Das Flag ankert an der LETZTEN Schicht des Tages (wie §8).
    if (minor && !sh.anomaly) {
      const next = sorted[i + 1]
      const istTagesletzte = !next || localDayKey(next.startMs) !== dayKey
      if (istTagesletzte) {
        const tagesSchichten = sorted.filter((x) => localDayKey(x.startMs) === dayKey)
        const spanne = sh.endMs - Math.min(...tagesSchichten.map((x) => x.startMs))
        if (spanne > JARBSCHG_MAX_SHIFT_SPAN_SECONDS * 1000) {
          flags.push({
            type: 'jugend_schichtzeit_over_10h',
            shiftStartMs: sh.startMs,
            shiftEndMs: sh.endMs,
            workedSeconds: Math.floor(spanne / 1000),
            anomaly: tagesSchichten.some((x) => x.anomaly),
          })
        }
      }
    }
    // §14 JArbSchG: Beschäftigung nur zwischen 06:00 und 20:00 (lokal). Eine Schicht über die lokale
    // Mitternacht liegt zwangsläufig außerhalb; sonst zählt Beginn vor 06:00 oder Ende nach 20:00.
    // Ende EXAKT 00:00 gehört zum Folgetag (Arbeit bis 24:00 ist nie legal) → bewusst KEIN endMs-1.
    if ((minor || schwanger) && !sh.anomaly) {
      const crossesDay = localDayKey(sh.startMs) !== localDayKey(sh.endMs)
      if (crossesDay || localMinutesOfDay(sh.startMs) < JARBSCHG_DAY_START_MIN || localMinutesOfDay(sh.endMs) > JARBSCHG_DAY_END_MIN) {
        flags.push({ type: minor ? 'jugend_nachtarbeit' : 'muschg_nachtarbeit', shiftStartMs: sh.startMs, shiftEndMs: sh.endMs, anomaly: sh.anomaly })
      }
    }
    const prev = sorted[i - 1]
    // Ruhezeit (§5 ArbZG 11h / §13 JArbSchG 12h) gilt NUR zwischen Arbeitstagen, NICHT innerhalb eines Tages:
    // zwei Schichten, die am SELBEN Kalendertag BEGINNEN (geteilte Schicht / Mittags-Ausstempeln), sind EINE
    // tägliche Arbeitszeit mit Pause, keine Ruhezeit — sonst falscher Alarm bei einem legalen Split (Review-Fund R4).
    if (prev && !prev.anomaly && localDayKey(prev.startMs) !== localDayKey(sh.startMs)) {
      const minRest = minor ? JARBSCHG_MIN_REST_SECONDS : ARBZG_MIN_REST_SECONDS
      const rest = sh.startMs - prev.endMs
      if (rest < minRest * 1000) {
        flags.push({ type: minor ? 'jugend_rest_short' : 'rest_short', shiftStartMs: sh.startMs, shiftEndMs: sh.endMs, restSeconds: Math.max(0, Math.floor(rest / 1000)) })
      }
    }
  }
  return flags
}

// ---- Proaktive Erinnerungen an den Mitarbeiter (R6-P4, Cron) ----
// Anders als arbzgViolations (Rückschau für den Chef) sollen diese den MA RECHTZEITIG stupsen, BEVOR eine Grenze
// reißt. Sie werden auf der OFFENEN Schicht berechnet: die heutigen Stempel + ein synthetisches „gehen" bei jetzt
// → derselbe getestete workShifts()-Motor liefert den aktuellen Stand.

/** Pausen-Stups: kurz vor der 6-h-am-Stück-Grenze (§4) — Puffer, damit die Pause noch rechtzeitig kommt. */
export const ARBZG_BREAK_NUDGE_SECONDS = Math.round(5.5 * 3600)
/** 10-h-Warnung (§3): ab 9,5 h Arbeitszeit „Feierabend einplanen". */
export const ARBZG_WARN_10H_SECONDS = Math.round(9.5 * 3600)
/** „Ausstempeln vergessen?": ab 12 h eingestempelt (deutlich über dem gesetzlichen Maximum → wohl vergessen). */
export const CLOCKOUT_REMINDER_SECONDS = 12 * 3600
/** Jugend-Nudges (unter 18, JArbSchG): Pause vor der 4,5-h-Grenze (§11 Abs. 2), 8-h-Warnung vor §8. */
export const JARBSCHG_BREAK_NUDGE_SECONDS = 4 * 3600
export const JARBSCHG_WARN_8H_SECONDS = Math.round(7.5 * 3600)

export type ReminderKind = 'break_due' | 'approaching_10h' | 'approaching_8h' | 'approaching_8_5h' | 'clockout_forgotten'

export interface DueReminder {
  kind: ReminderKind
  /** Start der laufenden Schicht (DB-unabhängiger ms-Anker) — dient als Dedup-Schlüssel (einmal pro Schicht). */
  shiftStartMs: number
  workedSeconds: number
  stretchSeconds: number
}

/**
 * Welche proaktiven Erinnerungen sind für den Nutzer JETZT fällig? Leer, wenn er nicht eingestempelt ist ('aus')
 * oder die Schicht anomal ist (vermutlich vergessenes Ausstempeln → Zeiten unzuverlässig). „Pause fällig" nur im
 * Arbeitsstatus 'an' (auf Pause läuft der „am Stück"-Zähler ohnehin nicht weiter).
 */
export function dueReminders(
  stamps: readonly Stamp[],
  nowMs: number,
  opts: WorkedOptions = {},
  /** Geburtsdatum (Phase-1/A2): unter 18 gelten die strengeren Jugend-Schwellen (§11/§8 JArbSchG). */
  birthdate?: string | null,
  /** MuSchG (Phase 2/3): Schwangerschaft → 8,5-h-Tagesgrenze, Warnung ab 8 h Tagessumme. */
  mutterschutz?: boolean,
): DueReminder[] {
  // Bewusst attendanceStatus (UNGEFENSTERT) statt currentStatus: das 18-h-Fenster würde bei einer sehr langen
  // Schicht (vergessenes Ausstempeln, 19 h) den kommen-Stempel verlieren → gerade dann keine Erinnerung.
  const status = attendanceStatus(stamps)
  if (status === 'aus') return []
  const closed: Stamp[] = [...stamps, { ts: nowMs, kind: 'gehen' }]
  const shifts = workShifts(closed, opts)
  const current = shifts[shifts.length - 1]
  if (!current) return []
  const minor = isMinorOn(birthdate, localDayKey(current.startMs))
  const base = { shiftStartMs: current.startMs, workedSeconds: current.workedSeconds, stretchSeconds: current.longestWorkStretchSeconds }
  const out: DueReminder[] = []
  // „Ausstempeln vergessen?" IST genau der überlange/Anomalie-Fall → hier NICHT auf !anomaly gaten.
  if (current.workedSeconds >= CLOCKOUT_REMINDER_SECONDS) out.push({ kind: 'clockout_forgotten', ...base })
  // Die legal getakteten Nudges nur auf VERLÄSSLICHEN Zeiten (keine Anomalie durch vergessenes Ausstempeln).
  if (!current.anomaly) {
    const breakNudge = minor ? JARBSCHG_BREAK_NUDGE_SECONDS : ARBZG_BREAK_NUDGE_SECONDS
    if (status === 'an' && current.longestWorkStretchSeconds >= breakNudge) out.push({ kind: 'break_due', ...base })
    if (minor) {
      // §8 deckelt den TAG: nach einem Mittags-Split (gehen+kommen) zählt die SUMME der heutigen Schichten,
      // nicht nur die laufende — sonst resettet der Split die 8-h-Warnung.
      const dayKey = localDayKey(current.startMs)
      const todayWorked = shifts.reduce((s, sh) => (localDayKey(sh.startMs) === dayKey ? s + sh.workedSeconds : s), 0)
      if (todayWorked >= JARBSCHG_WARN_8H_SECONDS) out.push({ kind: 'approaching_8h', ...base, workedSeconds: todayWorked })
    } else if (mutterschutz) {
      const dayKey = localDayKey(current.startMs)
      const todayWorked = shifts.reduce((s, sh) => (localDayKey(sh.startMs) === dayKey ? s + sh.workedSeconds : s), 0)
      if (todayWorked >= MUSCHG_WARN_SECONDS) out.push({ kind: 'approaching_8_5h', ...base, workedSeconds: todayWorked })
    } else if (current.workedSeconds >= ARBZG_WARN_10H_SECONDS) {
      out.push({ kind: 'approaching_10h', ...base })
    }
  }
  return out
}

// ---- Zeitzonen-Helfer (für die lokale Tages-Zuordnung) ----

interface WallClock {
  y: number
  mo: number
  d: number
  h: number
  mi: number
  s: number
}

function wallClock(epochMs: number, timeZone: string): WallClock {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(epochMs))
  const get = (type: Intl.DateTimeFormatPartTypes): number => {
    const p = parts.find((x) => x.type === type)
    return p ? parseInt(p.value, 10) : 0
  }
  return { y: get('year'), mo: get('month'), d: get('day'), h: get('hour'), mi: get('minute'), s: get('second') }
}

/** Lokaler Kalendertag eines Zeitpunkts als 'YYYY-MM-DD' (zeitzonenkorrekt). */
export function localDayKey(epochMs: number, timeZone: string = DEFAULT_TIME_ZONE): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(epochMs))
}

/** Wanduhrzeit 'HH:MM' eines Zeitpunkts in der Werkstatt-Zeitzone (Anzeige von Beginn/Ende/Stempeln).
 *  Die UI darf Zeitpunkte NIE über die Geräte-Zeitzone rendern — Server-Tageszeilen sind tz-fixiert. */
export function formatWallClock(epochMs: number, timeZone: string = DEFAULT_TIME_ZONE): string {
  const { h, mi } = wallClock(epochMs, timeZone)
  return `${String(h).padStart(2, '0')}:${String(mi).padStart(2, '0')}`
}

/**
 * Wandzeit → UTC: epoch ms des Moments, an dem die Uhr in `timeZone` am Tag `day` genau `HH:MM` zeigt
 * (Gegenstück zu localDayKey/formatWallClock; für Chef-Nachträge, die als lokale Uhrzeit eingegeben
 * werden). Zwei-Pass-Offset-Korrektur macht die Umrechnung DST-robust; bei der nicht existierenden
 * Frühjahrs-Sprungstunde landet das Ergebnis auf der real existierenden Nachbarzeit.
 */
export function wallTimeToUtcMs(day: string, time: string, timeZone: string = DEFAULT_TIME_ZONE): number {
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day)
  const tm = /^(\d{1,2}):(\d{2})$/.exec(time)
  if (!dm || !tm) return Number.NaN
  const wanted = Date.UTC(Number(dm[1]), Number(dm[2]) - 1, Number(dm[3]), Number(tm[1]), Number(tm[2]))
  // Wanduhr an einem Kandidaten ablesen und als UTC re-interpretieren → Differenz = Zonen-Offset dort.
  const wallAsUtc = (ms: number): number => {
    const w = wallClock(ms, timeZone)
    return Date.UTC(w.y, w.mo - 1, w.d, w.h, w.mi, w.s)
  }
  let candidate = wanted - (wallAsUtc(wanted) - wanted)
  const diff = wanted - wallAsUtc(candidate)
  if (diff !== 0) candidate += diff // zweiter Pass für DST-Kanten
  return candidate
}

/** epoch ms der lokalen Mitternacht (Beginn des lokalen Tages, der epochMs enthält). */
export function startOfLocalDay(epochMs: number, timeZone: string = DEFAULT_TIME_ZONE): number {
  const { h, mi, s } = wallClock(epochMs, timeZone)
  const msIntoDay = ((h * 60 + mi) * 60 + s) * 1000 + (((epochMs % 1000) + 1000) % 1000)
  return epochMs - msIntoDay
}

/** Beginn des FOLGENDEN lokalen Tages — DST-robust (23h/25h-Tage werden korrekt behandelt). */
function startOfNextLocalDay(epochMs: number, timeZone: string): number {
  const approxNext = startOfLocalDay(epochMs, timeZone) + DAY_MS
  // +2h überspringt eine eventuelle DST-Verschiebung, dann auf den exakten Tagesbeginn zurücksnappen.
  return startOfLocalDay(approxNext + 2 * HOUR_MS, timeZone)
}

/**
 * Gearbeitete Sekunden je LOKALEM Kalendertag. Segmente, die über die lokale Mitternacht laufen,
 * werden anteilig auf beide Tage verteilt → Statistik/Timesheet decken sich mit dem Monatsnachweis,
 * und eine echte Nachtschicht wird voll gezählt (Fix B-5).
 */
export function workedSecondsByLocalDay(
  stamps: readonly Stamp[],
  timeZone: string = DEFAULT_TIME_ZONE,
  opts: WorkedOptions = {},
): Record<string, number> {
  const { segments } = workedSegments(stamps, opts)
  const byDay: Record<string, number> = {}
  for (const seg of segments) {
    let cursor = seg.from
    while (cursor < seg.to) {
      const boundary = Math.min(seg.to, startOfNextLocalDay(cursor, timeZone))
      const day = localDayKey(cursor, timeZone)
      byDay[day] = (byDay[day] ?? 0) + Math.floor((boundary - cursor) / 1000)
      cursor = boundary
    }
  }
  return byDay
}

/** Gesamt-Arbeitssekunden über alle (geschlossenen) Segmente — identisch zu workedSeconds().seconds. */
export function totalWorkedSeconds(stamps: readonly Stamp[], opts: WorkedOptions = {}): number {
  return workedSeconds(stamps, opts).seconds
}

export interface AttendanceSnapshot {
  status: AttStatus
  /** Heute (Lokaltag) abgeschlossene Arbeitssekunden, an der lokalen Mitternacht gesplittet (B-5). */
  todaySeconds: number
  /** epoch ms: ab hier live in „heute" mitzählen, auf die lokale Mitternacht geklemmt; null außer bei Status 'an'. */
  liveSince: number | null
}

/**
 * Heutiger Anwesenheits-Schnappschuss für ein Set Stempel — die EINE Ableitung, die Header-Widget (eigener
 * Stand) und Team-Übersicht teilen. `todaySeconds` zählt nur GESCHLOSSENE Segmente; das offene Segment trägt
 * der Live-Zähler über `now - liveSince`. `liveSince` ist auf die heutige lokale Mitternacht geklemmt, damit
 * eine seit gestern laufende Schicht „heute" nicht doppelt anrechnet (B-5). I/O-frei → direkt unit-testbar.
 */
export function attendanceSnapshot(
  stamps: readonly Stamp[],
  nowMs: number,
  timeZone: string = DEFAULT_TIME_ZONE,
  opts: WorkedOptions = {},
): AttendanceSnapshot {
  const status = currentStatus(stamps, nowMs, DEFAULT_STATUS_WINDOW_HOURS)
  const { openSince } = workedSegments(stamps, opts)
  const todaySeconds = workedSecondsByLocalDay(stamps, timeZone, opts)[localDayKey(nowMs, timeZone)] ?? 0
  const liveSince = status === 'an' && openSince != null ? Math.max(openSince, startOfLocalDay(nowMs, timeZone)) : null
  return { status, todaySeconds, liveSince }
}

/** Eine Tageszeile des Zeitnachweises: Beginn/Ende (erster/letzter Stempel des Lokaltags) + Netto-Dauer. */
export interface AttendanceDay {
  /** Lokaler Kalendertag 'YYYY-MM-DD'. */
  day: string
  /** epoch ms des ersten Stempels dieses Lokaltags (Beginn); null, wenn der Tag Arbeitszeit nur aus einem
   *  über Mitternacht laufenden Segment erbt (kein eigener Stempel an diesem Kalendertag). */
  startMs: number | null
  /** epoch ms des letzten Stempels dieses Lokaltags (Ende); null wie startMs. */
  endMs: number | null
  /** Netto-Arbeitssekunden dieses Lokaltags, an der lokalen Mitternacht gesplittet (B-5). */
  workedSeconds: number
}

/**
 * Verdichtet (nicht entwertete) Stempel zum Tages-Zeitnachweis: Beginn, Ende und Dauer je LOKALEM Kalendertag
 * — genau das von EuGH/BAG geforderte Trio. Die Dauer kommt aus workedSecondsByLocalDay (Mitternachts-Split,
 * B-5); Beginn/Ende sind der erste/letzte Stempel dieses Lokaltags. Ein über Mitternacht laufendes Segment
 * gibt dem Folgetag Arbeitssekunden ohne eigenen Stempel → dort startMs/endMs null. I/O-frei, direkt testbar.
 * Entwertete Stempel müssen vom Aufrufer VORHER ausgefiltert werden (zählen nicht zur Arbeitszeit).
 */
export function attendanceReportDays(
  stamps: readonly Stamp[],
  timeZone: string = DEFAULT_TIME_ZONE,
  opts: WorkedOptions = {},
): AttendanceDay[] {
  const byDaySeconds = workedSecondsByLocalDay(stamps, timeZone, opts)
  const firstLast: Record<string, { start: number; end: number }> = {}
  for (const s of stamps) {
    const day = localDayKey(s.ts, timeZone)
    const fl = firstLast[day]
    if (!fl) firstLast[day] = { start: s.ts, end: s.ts }
    else {
      if (s.ts < fl.start) fl.start = s.ts
      if (s.ts > fl.end) fl.end = s.ts
    }
  }
  const days = new Set<string>([...Object.keys(byDaySeconds), ...Object.keys(firstLast)])
  return [...days].sort().map((day) => ({
    day,
    startMs: firstLast[day]?.start ?? null,
    endMs: firstLast[day]?.end ?? null,
    workedSeconds: byDaySeconds[day] ?? 0,
  }))
}

/**
 * Gearbeitete Sekunden über einen LOKALEN Tagesbereich [fromDay, toDay] (inklusiv, 'YYYY-MM-DD'). Paart die
 * Stempel zuerst vollständig (workedSecondsByLocalDay, B-5-Mitternachts-Split) und summiert dann nur die Tage
 * im Bereich. Der Aufrufer muss die Stempel über die Bereichsgrenze hinaus puffern, damit ein über die Kante
 * laufendes Segment seinen öffnenden Stempel behält — sonst zählt es 0 (Review-Fund W9 stats-ist7).
 */
export function workedSecondsInDayRange(
  stamps: readonly Stamp[],
  fromDay: string,
  toDay: string,
  timeZone: string = DEFAULT_TIME_ZONE,
  opts: WorkedOptions = {},
): number {
  const byDay = workedSecondsByLocalDay(stamps, timeZone, opts)
  let sum = 0
  for (const [day, sec] of Object.entries(byDay)) if (day >= fromDay && day <= toDay) sum += sec
  return sum
}

// ---- Mitarbeiter-Korrekturanfragen (W9.3) ----

/** Arten einer Korrekturanfrage (Alt-Parität). 'sonstiges' verlangt eine Freitext-Beschreibung statt Uhrzeit. */
export const CORRECTION_KINDS = [
  'kommen_vergessen',
  'gehen_vergessen',
  'kommen_aendern',
  'gehen_aendern',
  'pause',
  'sonstiges',
] as const
export type CorrectionKind = (typeof CORRECTION_KINDS)[number]

export function isCorrectionKind(value: unknown): value is CorrectionKind {
  return typeof value === 'string' && (CORRECTION_KINDS as readonly string[]).includes(value)
}

// ---- Soll/Ist-Wochennachweis (Dienstplan vs. Stempel, W9.4) ----

/** Eine Dienstplan-Zeile (Wochenmuster). weekday 0=Mo … 6=So. start/end/break in Minuten ab Mitternacht. */
export interface WorkPlanDay {
  weekday: number
  startMin: number | null
  endMin: number | null
  breakMin: number | null
  /** Dienstplan-Versionierung (Phase-1/1b): ab wann diese Version gilt ('YYYY-MM-DD'). Eine „Version" ist der
   *  KOMPLETTE Wochen-Schnappschuss aller Zeilen mit demselben validFrom. Fehlt das Feld (Alt-Aufrufer/Tests),
   *  zählt die Zeile zur Basis-Version (immer gültig) — rückwärtskompatibel. */
  validFrom?: string | null
}

/** Netto-Sekunden einer Dienstplan-Zeile (Spanne minus Pause); leerer Tag (null-Zeiten) = 0. */
function planNetSeconds(p: WorkPlanDay): number {
  if (p.startMin == null || p.endMin == null) return 0
  return Math.max(0, p.endMin - p.startMin - (p.breakMin ?? 0)) * 60
}

/**
 * Die am Kalendertag `dayKey` WIRKSAMEN Dienstplan-Zeilen: die Version mit dem größten validFrom ≤ dayKey
 * (Zeilen ohne validFrom = Basis-Version, sortiert vor jedem echten Datum). Leere Liste, wenn der Tag vor
 * JEDER Version liegt (z. B. vor der Einstellung des Mitarbeiters) → Soll 0. Ein Wochentag ohne Zeile in der
 * wirksamen Version ist KEIN Solltag — genau so bleibt eine rückwirkend unveränderte Vergangenheit stabil,
 * wenn der Chef das Muster mit „gültig ab" ändert (GoBD-Befund A1, docs/DESIGN-zeit-phase1-monatsabschluss.md).
 */
export function effectiveWorkPlan(plans: readonly WorkPlanDay[], dayKey: string): WorkPlanDay[] {
  let best: string | null = null // größtes validFrom ≤ dayKey; '' = Basis-Version (lexikografisch vor jedem Datum)
  for (const p of plans) {
    const vf = p.validFrom ?? ''
    if (vf > dayKey) continue
    if (best === null || vf > best) best = vf
  }
  if (best === null) return []
  return plans.filter((p) => (p.validFrom ?? '') === best)
}

/** Tages-Soll (Sekunden) am Kalendertag `dayKey` aus der dort wirksamen Plan-Version. */
export function sollSecondsForDay(plans: readonly WorkPlanDay[], dayKey: string): number {
  const weekday = (new Date(dayKey + 'T12:00:00Z').getUTCDay() + 6) % 7 // 0=Mo … 6=So
  let sec = 0
  for (const p of effectiveWorkPlan(plans, dayKey)) if (p.weekday === weekday) sec += planNetSeconds(p)
  return sec
}

/**
 * DURCHSCHNITTLICHE TÄGLICHE ARBEITSZEIT (Sekunden) aus der am `dayKey` wirksamen Plan-Version:
 * Wochensoll / Anzahl der Solltage. 0, wenn es keinen Solltag gibt.
 *
 * Das ist die gesetzliche Anrechnungsbasis für Berufsschultage — § 9 Abs. 2 Nr. 1 JArbSchG und § 15 Abs. 2
 * Nr. 2 BBiG sagen wörtlich „mit der durchschnittlichen täglichen Arbeitszeit", NICHT „mit dem Soll des
 * betroffenen Wochentags". Bei gleichmäßiger Woche ist beides identisch; bei ungleicher (Mo–Do 9 h, Fr 4 h)
 * bekäme ein Azubi mit Schultag am Freitag sonst 4 h statt der zustehenden 8 h angerechnet.
 *
 * (Die früher im Gesetz stehenden Pauschalen „8 Stunden" / „40 Stunden" sind zum 01.01.2020 durch das
 * BBiMoG entfallen — sie durch feste Konstanten nachzubilden wäre heute falsch, u. a. bei Teilzeitausbildung.)
 */
export function averageDailySollSeconds(plans: readonly WorkPlanDay[], dayKey: string): number {
  const byWeekday = new Map<number, number>()
  for (const p of effectiveWorkPlan(plans, dayKey)) {
    const sec = planNetSeconds(p)
    if (sec <= 0) continue
    byWeekday.set(p.weekday, (byWeekday.get(p.weekday) ?? 0) + sec)
  }
  if (byWeekday.size === 0) return 0
  let total = 0
  for (const sec of byWeekday.values()) total += sec
  return Math.round(total / byWeekday.size)
}

/** Erster Tag des Folgemonats von `dayKey` — der Standard für „gültig ab" einer Dienstplanänderung
 *  (der laufende Monat bleibt stabil; Design-Entscheidung Simon 26.07.2026). */
export function firstOfNextMonth(dayKey: string): string {
  const y = Number(dayKey.slice(0, 4))
  const m = Number(dayKey.slice(5, 7))
  const ny = m === 12 ? y + 1 : y
  const nm = m === 12 ? 1 : m + 1
  return `${ny}-${String(nm).padStart(2, '0')}-01`
}

// ---- Zeitkonto-Perioden (Monatsabschluss, Phase-1/Teilschritt 2) ----

/** Gültiges Perioden-Label 'YYYY-MM'? Jahr plausibel begrenzt (2000–2099) — ein Erst-Abschluss auf z. B.
 *  '0001-01' würde die sequenzielle Konto-Kette sonst irreversibel vergiften (Review-Fund). */
export function isPeriod(v: unknown): v is string {
  if (typeof v !== 'string' || !/^20\d{2}-(0[1-9]|1[0-2])$/.test(v)) return false
  return true
}

/** Folgemonat einer Periode: '2026-12' → '2027-01'. */
export function nextPeriod(period: string): string {
  const y = Number(period.slice(0, 4))
  const m = Number(period.slice(5, 7))
  return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`
}

/** Vormonat einer Periode: '2027-01' → '2026-12'. */
export function previousPeriod(period: string): string {
  const y = Number(period.slice(0, 4))
  const m = Number(period.slice(5, 7))
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`
}

/** Kalender-Spanne einer Periode (inklusiv): '2026-02' → { from: '2026-02-01', to: '2026-02-28' }. */
export function periodRange(period: string): { from: string; to: string } {
  const y = Number(period.slice(0, 4))
  const m = Number(period.slice(5, 7))
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate() // Tag 0 des Folgemonats = letzter Tag dieses Monats
  return { from: `${period}-01`, to: `${period}-${String(lastDay).padStart(2, '0')}` }
}

/** Soll/Ist einer Kalenderwoche (Mo-beginnend). Sekunden. */
export interface TimesheetWeek {
  /** Montag dieser Woche, 'YYYY-MM-DD' (lokaler Wochenstart). */
  weekStart: string
  /** Plan-Soll der im Zeitraum liegenden Tage dieser Woche (netto, Pause abgezogen). */
  sollSeconds: number
  /** Tatsächlich gearbeitet (Stempel, B-5-Mitternachts-Split) an den im Zeitraum liegenden Tagen. */
  istSeconds: number
}

/** Datum 'YYYY-MM-DD' aus epoch ms (UTC-Tageslabel — für die kalendarische Tag-für-Tag-Iteration). */
function utcDayKey(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10)
}

/**
 * Soll/Ist je Kalenderwoche über [from, to] (lokale Kalendertage, inklusiv). Pro Tag: Soll = Dienstplan-Netto
 * dieses Wochentags (0 ohne Plan), Ist = gearbeitete Sekunden dieses Lokaltags (B-5-Mitternachts-Split). Beide
 * werden je Mo-beginnender Woche summiert → Soll und Ist decken exakt dieselben Tage (auch bei Teilwochen am
 * Rand). I/O-frei. Entwertete Stempel muss der Aufrufer vorher ausfiltern. Der Aufrufer begrenzt den Zeitraum.
 */
export interface TimesheetDay {
  dayKey: string
  /** Montags-Datum der Woche, zu der der Tag gehört. */
  weekStart: string
  /** Tagessoll NACH Feiertags-Ausfallprinzip. */
  sollSeconds: number
  /** Soll nach Abwesenheits-Wirkung ('soll_null' kürzt). */
  effectiveSollSeconds: number
  /** Gestempelte Zeit dieses Lokaltags (B-5-Mitternachts-Split). */
  workedSeconds: number
  /** Angerechnete Gutschrift der Abwesenheit. */
  creditedSeconds: number
  /** GANZTÄGIG abwesend laut Erfassung — und trotzdem gestempelt. Ein Widerspruch, den nur ein Mensch klärt. */
  absenceConflict: boolean
  /** Mehrere Abwesenheiten desselben Tages ergeben zusammen MEHR als einen Tag (Faktor-Summe > 1) — also
   *  z. B. ein ganzer Urlaubstag und ein ganzer Schultag. Ebenfalls ein reiner Erfassungsfehler. */
  absenceOverlapConflict: boolean
  /** Kollisionsgruppe der FÜHRENDEN Wirkung — nur für Anzeige/Diagnose.
   *  ⚠️ NICHT für Rechenzwecke benutzen: an einem Tag mit halbem Schultag + halbem Urlaubstag führt
   *  'abwesend', obwohl Ausbildungszeit anzurechnen ist. Dafür gibt es creditedAusbildungSeconds. */
  absenceGroup?: CollisionGroup
  /** Der Anteil der Gutschrift, der aus der Gruppe 'ausbildung' stammt (§ 9 Abs. 2 JArbSchG / § 15 Abs. 2
   *  BBiG). Getrennt ausgewiesen, weil NUR dieser Anteil Arbeitszeit i. S. d. § 4 Abs. 1 JArbSchG ist —
   *  Urlaub und Krankheit sind es nicht. */
  creditedAusbildungSeconds: number
  /** Tages-Soll aus dem Dienstplan OHNE Feiertagskürzung. `sollSeconds` ist am Feiertag per
   *  Ausfallprinzip 0; für § 4 Abs. 4 Satz 2 (die am Feiertag ausgefallene Zeit wird auf die 40-h-Woche
   *  angerechnet) braucht der Wächter dagegen den ungekürzten Wert. */
  planSollSeconds: number
  /** Faktor der Ausbildungs-Abwesenheit (0 / 0,5 / 1), unabhängig von der Gutschrift. Ein Berufsschultag an
   *  einem planfreien Tag erzeugt keine Gutschrift, ist aber trotzdem ein Beschäftigungstag. */
  ausbildungFactor: number
  /** Anteil des Tages (0 / 0,5 / 1), der durch eine Abwesenheit entschuldigt ist, die KEIN
   *  Arbeitszeit-Ausgleich im Sinne des § 3 Satz 2 ArbZG ist — Urlaub, Krankheit, Elternzeit, unbezahlt.
   *  Der Durchschnittswächter stellt solche Tage neutral (raus aus Zähler UND Nenner), weil ein
   *  eigenständiger Anspruch keine Mehrarbeit abarbeiten darf. Siehe absenceNeutralFactor(). */
  absenceNeutralFactor: number
}

/**
 * Tag-für-Tag-Durchlauf über [from, to] — die EINE Wahrheit für Soll, Ist und Abwesenheits-Wirkung.
 * weeklyTimesheet() aggregiert daraus die Wochen, absenceStampConflicts() filtert die Widersprüche; so gibt
 * es keine zweite Kopie dieser Rechnung.
 */
export function timesheetDays(
  plans: readonly WorkPlanDay[],
  stamps: readonly Stamp[],
  from: string,
  to: string,
  timeZone: string = DEFAULT_TIME_ZONE,
  opts: WorkedOptions = {},
  holidayDays: ReadonlySet<string> = new Set(),
  absenceDays: ReadonlyMap<string, AbsenceDayEffect> = new Map(),
): TimesheetDay[] {
  const istByDay = workedSecondsByLocalDay(stamps, timeZone, opts)
  const out: TimesheetDay[] = []
  const fromMs = Date.parse(from + 'T00:00:00Z')
  const toMs = Date.parse(to + 'T00:00:00Z')
  for (let d = fromMs; d <= toMs; d += DAY_MS) {
    const dayKey = utcDayKey(d)
    const jsDay = new Date(d).getUTCDay() // 0=So … 6=Sa
    const planWeekday = (jsDay + 6) % 7 // → 0=Mo … 6=So
    // Soll PRO TAG aus der an diesem Tag wirksamen Plan-Version (1b) — eine spätere Dienstplanänderung
    // („gültig ab") verschiebt vergangene Tage damit NICHT mehr. Feiertag → Ausfallprinzip (Soll 0).
    const planSoll = sollSecondsForDay(plans, dayKey)
    const daySoll = holidayDays.has(dayKey) ? 0 : planSoll
    const abs = absenceDays.get(dayKey)
    const worked = istByDay[dayKey] ?? 0

    // Gutschrift und gestempelte Zeit ADDIEREN sich — und das ist richtig so.
    //
    // Der naheliegende Verdacht lautet: „8 h Berufsschule gutgeschrieben plus 4 h gestempelt ergibt 12 h bei
    // 8 h Soll, also vier Plusstunden aus dem Nichts." Nachgerechnet stimmt das nicht. Berufsschulzeit IST
    // nach § 15 BBiG Ausbildungszeit; wer danach noch vier Stunden in der Werkstatt steht, hat real zwölf
    // Stunden geleistet. Dass daraus ein Plus wird, ist korrekt — und der JArbSchG-Verstoß, der darin steckt,
    // soll sichtbar bleiben, nicht weggerechnet werden. Beim Urlaub gilt dasselbe: das Kontingent wird per
    // vacationConsumedTenths ohnehin voll verbraucht (die Funktion sieht keine Stempel), der Mitarbeiter hat
    // seinen Urlaubstag also geopfert — die geleisteten Stunden gehören ihm.
    //
    // Ein Deckel („Gutschrift füllt nur bis zum Tagessoll auf") wäre die schlechtere Wahl: Er macht jede
    // gestempelte Stunde unterhalb des Solls wertlos, löscht damit real geleistete und vergütungspflichtige
    // Arbeitszeit aus dem Ist (§ 16 ArbZG Aufzeichnungspflicht) und trifft beim halben Schultag plus langem
    // Werkstatt-Nachmittag ausgerechnet den Normalfall eines Azubis.
    //
    // Was WIRKLICH faul ist, ist kein Rechen-, sondern ein Erfassungsfehler: wer GANZTÄGIG als abwesend
    // eingetragen ist und trotzdem stempelt, hat einen der beiden Einträge falsch (meist hätte es ein halber
    // Tag sein müssen). Das wird gemeldet statt stillschweigend verrechnet — korrigieren kann es nur ein Mensch.
    //
    // `stamp_behavior` macht diese Entscheidung explizit: 'add' ist der hier begründete Default, 'fill' eine
    // bewusste Betriebsoption, die dank Zeilen-Snapshot nie rückwirkend wirkt.
    //
    // ANRECHNUNGSBASIS (Azubi-Block): Berufsschulzeit wird nach § 9 Abs. 2 Nr. 1 JArbSchG / § 15 Abs. 2 Nr. 2
    // BBiG mit der DURCHSCHNITTLICHEN täglichen Arbeitszeit angerechnet, alles andere (Urlaub, Krank) mit dem
    // Soll DIESES Tages — dort ersetzt die Abwesenheit ja genau das, was an dem Tag zu leisten gewesen wäre.
    // ⚠️ Beides nur an einem echten Solltag: an einem Feiertag oder einem planfreien Tag ist daySoll 0, und
    // ein Durchschnittswert würde dort eine Gutschrift aus dem Nichts erzeugen (Doppel-Kredit).
    const f = creditFactors(abs)
    const grossAusbildung = daySoll > 0 ? Math.round(averageDailySollSeconds(plans, dayKey) * f.ausbildung) : 0
    const gross = daySoll > 0 ? grossAusbildung + Math.round(daySoll * f.sonstige) : 0
    const credited = abs?.stampBehavior === 'fill' ? Math.max(0, gross - worked) : gross
    // Der Ausbildungsanteil wird SEPARAT ausgewiesen (nicht über absenceGroup ableitbar: an einem Tag mit
    // halbem Schultag + halbem Urlaubstag führt 'abwesend', obwohl Ausbildungszeit anzurechnen ist).
    //
    // ⚠️ Diese Größe ist eine ARBEITSZEIT-Größe (§ 4 Abs. 1 / § 9 Abs. 2 JArbSchG), keine Zeitkonto-Größe:
    // sie wird auch an einem Tag OHNE Dienstplan-Soll gefüllt. Berufsschulzeit ist Ausbildungszeit,
    // unabhängig davon, ob für den Tag ein Soll geplant war — sonst fiele sie aus der 40-h-Woche. Das
    // ZEITKONTO (creditedSeconds) bleibt davon unberührt: dort gilt weiter der Solltag-Guard, damit an
    // einem Feiertag oder planfreien Tag keine Gutschrift aus dem Nichts entsteht.
    const ausbildungAnrechnung = holidayDays.has(dayKey) ? 0 : Math.round(averageDailySollSeconds(plans, dayKey) * f.ausbildung)
    const creditedAusbildung = abs?.stampBehavior === 'fill' ? Math.max(0, Math.min(grossAusbildung, gross - worked)) : ausbildungAnrechnung

    // 'soll_null' kürzt das Soll — ebenfalls nur bis zur vollen Tagesbreite (Faktor-Summe gedeckelt).
    const sollNullFactor = Math.min(
      1,
      (abs?.factorSum ?? 0) > 1
        ? abs?.effect === 'soll_null'
          ? abs.factor
          : 0
        : [abs, ...(abs?.others ?? [])].reduce((s, w) => s + (w?.effect === 'soll_null' ? w.factor : 0), 0),
    )

    out.push({
      dayKey,
      weekStart: utcDayKey(d - planWeekday * DAY_MS),
      sollSeconds: daySoll,
      effectiveSollSeconds: sollNullFactor > 0 ? Math.round(daySoll * (1 - sollNullFactor)) : daySoll,
      workedSeconds: worked,
      creditedSeconds: credited,
      creditedAusbildungSeconds: creditedAusbildung,
      planSollSeconds: planSoll,
      /** Faktor der Ausbildungs-Wirkung (0 / 0,5 / 1) — UNABHÄNGIG davon, ob daraus eine Gutschrift wurde.
       *  An einem planfreien Tag ist die Gutschrift 0, der Tag war aber trotzdem ein Berufsschultag und
       *  zählt für § 15 JArbSchG und § 15 BBiG. */
      ausbildungFactor: f.ausbildung,
      // Ganztägig als abwesend erfasst UND trotzdem gestempelt. Maßgeblich ist die SUMME der Gutschrift-
      // Faktoren, nicht der Faktor der führenden Zeile: ein halber Schultag plus ein halber Urlaubstag
      // ergibt ebenfalls einen ganzen abwesenden Tag — und genau diese Kombination ist seit dem Azubi-Block
      // der Normalfall. Für eine einzelne Ganztags-Zeile ist das Ergebnis unverändert.
      absenceConflict: f.ausbildung + f.sonstige >= 1 && worked > 0,
      // Zwei Einträge, die zusammen mehr als einen Tag ergeben (ganzer Urlaub + ganzer Schultag): einer davon
      // ist falsch erfasst. Zwei HALBE Tage sind ausdrücklich kein Widerspruch, sondern der Azubi-Normalfall.
      absenceOverlapConflict: (abs?.factorSum ?? 0) > 1,
      absenceGroup: abs?.collisionGroup,
      absenceNeutralFactor: absenceNeutralFactor(abs),
    })
  }
  return out
}

/**
 * Tage, an denen jemand GANZTÄGIG als abwesend eingetragen ist und trotzdem gestempelt hat — laut Erfassung
 * also gleichzeitig abwesend und anwesend. Einer der beiden Einträge ist falsch (meist hätte die Abwesenheit
 * ein halber Tag sein müssen). Die Zeiten selbst bleiben unangetastet: gerechnet wird ehrlich, was erfasst
 * wurde, und der Widerspruch geht an den Menschen, der ihn auflösen kann.
 *
 * Ein HALBER Abwesenheitstag mit gestempelter Zeit ist ausdrücklich KEIN Konflikt — Schule vormittags,
 * Werkstatt nachmittags ist der Normalfall eines Azubis.
 */
export function absenceStampConflicts(days: readonly TimesheetDay[]): string[] {
  return days.filter((d) => d.absenceConflict).map((d) => d.dayKey)
}

export function weeklyTimesheet(
  plans: readonly WorkPlanDay[],
  stamps: readonly Stamp[],
  from: string,
  to: string,
  timeZone: string = DEFAULT_TIME_ZONE,
  opts: WorkedOptions = {},
  /** Gesetzliche Feiertage ('YYYY-MM-DD') des Bundeslands. AUSFALLPRINZIP: an einem Feiertag ist das Tages-SOLL 0
   *  (auch wenn laut Dienstplan ein Arbeitstag wäre); geleistete Arbeit am Feiertag bleibt als IST → wird Plus. */
  holidayDays: ReadonlySet<string> = new Set(),
  /** Genehmigte Abwesenheiten je Tag (absenceDayMap): 'gutschrift' schreibt das Tagessoll als Ist gut,
   *  'soll_null' streicht es, 'saldo_abbau' (FZA) lässt beides — der Saldo sinkt von selbst. Basis ist
   *  IMMER das Soll NACH Feiertagsregel → kein Doppel-Kredit am Feiertag/Wochenende. */
  absenceDays: ReadonlyMap<string, AbsenceDayEffect> = new Map(),
): TimesheetWeek[] {
  const byWeek = new Map<string, { soll: number; ist: number }>()
  for (const day of timesheetDays(plans, stamps, from, to, timeZone, opts, holidayDays, absenceDays)) {
    const bucket = byWeek.get(day.weekStart) ?? { soll: 0, ist: 0 }
    bucket.soll += day.effectiveSollSeconds
    bucket.ist += day.creditedSeconds + day.workedSeconds
    byWeek.set(day.weekStart, bucket)
  }
  return [...byWeek.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([weekStart, v]) => ({ weekStart, sollSeconds: v.soll, istSeconds: v.ist }))
}

// ===== JArbSchG-PERIODEN-WÄCHTER (Azubi-Block, Phase 3 — docs/DESIGN-zeit-azubi.md) =====
//
// Ein ZWEITER Motor neben arbzgViolations, bewusst nicht darin: der Schicht-Motor bucht eine Schicht immer
// auf ihren START-Tag und kennt keine Woche. Eine Nachtschicht So 23:30 → Mo 04:00 landete dort komplett in
// der Vorwoche; timesheetDays splittet dagegen an der lokalen Mitternacht. Bei 39,5 h Mo–Sa meldete ein
// schichtbasierter Wochenmotor 44 h — einen Verstoß, den es nicht gibt.
//
// Der Wochenbegriff ist deshalb `TimesheetDay.weekStart` (Montag): das ist zugleich die LEGALDEFINITION des
// § 4 Abs. 4 Satz 1 JArbSchG und derselbe Begriff, auf dem der Hard-Close einfriert und den der Nachweis
// zeigt. Ein zweiter Wochenbegriff wäre per Konstruktion divergent.

/** § 8 Abs. 1 JArbSchG: 40 h je Woche. */
export const JARBSCHG_MAX_WEEK_SECONDS = 40 * 3600
/** § 15 Satz 1 JArbSchG: höchstens fünf Beschäftigungstage je Woche. */
export const JARBSCHG_MAX_DAYS_PER_WEEK = 5
/** § 16 Abs. 2 Satz 2 JArbSchG: mindestens zwei Samstage im Monat beschäftigungsfrei (SOLL-Vorschrift). */
export const JARBSCHG_FREE_SATURDAYS_PER_MONTH = 2

/**
 * ARBEITSZEIT im Sinne des § 4 Abs. 1 JArbSchG für einen Tag — und das ist bewusst NICHT das Zeitkonto-Ist.
 *
 * Das Zeitkonto-Ist (credited + worked) ist eine Vergütungsgröße: es enthält jede Gutschrift, auch Urlaub
 * und Krankheit. § 4 Abs. 1 stellt dagegen auf die tatsächliche BESCHÄFTIGUNG ab. Das Gesetz ordnet
 * Anrechnungsfiktionen für nicht gearbeitete Zeit nur an zwei Stellen an, und beide sind abschließend:
 * gesetzliche Feiertage (§ 4 Abs. 4 Satz 2) und Berufsschule (§ 9 Abs. 2). Urlaub und Krankheit gehören
 * nicht dazu — wer sie mitzählt, meldet bei „Mo Urlaub + Di–Fr je 8,5 h" (real 34 h) einen 42-h-Verstoß.
 */
function jarbschgArbeitszeit(d: TimesheetDay, holidayDays: ReadonlySet<string>): number {
  // ⚠️ NICHT über absenceGroup (das ist nur die FÜHRENDE Wirkung): bei halbem Schultag + halbem Urlaubstag
  // führt 'abwesend', und die anzurechnende Berufsschulzeit fiele komplett aus der Wochensumme.
  const ausbildung = d.creditedAusbildungSeconds
  // § 4 Abs. 4 Satz 2: die an einem Feiertag AUSGEFALLENE Arbeitszeit wird auf die Wochenarbeitszeit
  // angerechnet — in einer Feiertagswoche steht also weniger tatsächliche Arbeitszeit zur Verfügung.
  // ⚠️ planSollSeconds, NICHT sollSeconds: letzteres ist am Feiertag per Ausfallprinzip immer 0, der Term
  // wäre also tot. Und nur der AUSGEFALLENE Teil zählt — wer am Feiertag gearbeitet hat, dessen Stunden
  // stehen schon in workedSeconds und dürfen nicht doppelt kommen.
  const feiertagsAusfall = holidayDays.has(d.dayKey) ? Math.max(0, d.planSollSeconds - d.workedSeconds) : 0
  return d.workedSeconds + ausbildung + feiertagsAusfall
}

/** Beschäftigungstag i. S. d. § 15 Satz 1: tatsächlich gearbeitet ODER Berufsschule (Ausbildungszeit).
 *  Urlaub und Krankheit sind das Gegenteil von Beschäftigung und zählen nicht mit.
 *  Der Ausbildungs-FAKTOR statt der Gutschrift: an einem planfreien Tag entsteht keine Gutschrift, der
 *  Schultag war trotzdem ein Beschäftigungstag. */
function istBeschaeftigungstag(d: TimesheetDay): boolean {
  return d.workedSeconds > 0 || d.ausbildungFactor > 0
}

const dayStartMs = (dayKey: string): number => Date.parse(dayKey + 'T00:00:00Z')
const monthOf = (dayKey: string): string => dayKey.slice(0, 7)

/** Samstage eines Kalendermonats ('YYYY-MM') — aus dem KALENDER, nicht aus dem übergebenen Ausschnitt. */
function saturdaysInMonth(period: string): string[] {
  const out: string[] = []
  const first = Date.parse(period + '-01T00:00:00Z')
  if (!Number.isFinite(first)) return out
  for (let d = first; utcDayKey(d).slice(0, 7) === period; d += DAY_MS) {
    if (new Date(d).getUTCDay() === 6) out.push(utcDayKey(d))
  }
  return out
}

export interface PeriodViolationOptions {
  birthdate?: string | null
  /** Ausbildungsverhältnis — steuert AUSSCHLIESSLICH die BBiG-Flags, nie eine JArbSchG-Mengengrenze. */
  ausbildungVon?: string | null
  ausbildungBis?: string | null
  /** Gesetzliche Feiertage im Bereich (§ 18 JArbSchG + § 4 Abs. 4 Satz 2). */
  holidayDays?: ReadonlySet<string>
  /** Tage mit anomaler Schicht (vermutlich vergessenes Ausstempeln) — deren Zahlen sind unzuverlässig. */
  anomalyDays?: ReadonlySet<string>
}

/**
 * Wochen-, Monats- und Tagesregeln des JArbSchG (plus § 15 BBiG), die der Schicht-Motor strukturell nicht
 * sehen kann. I/O-frei; Eingabe ist ausschließlich der Output von timesheetDays().
 *
 * ACHSEN-TRENNUNG (verbindlich): alle `jugend_*`-Flags hängen am ALTER am jeweiligen Tag (§ 1 Abs. 1
 * JArbSchG knüpft am Alter an, nicht am Vertrag — auch der Ferienjobber ohne Ausbildungsvertrag fällt
 * darunter). `azubi_*` hängt am AUSBILDUNGSVERHÄLTNIS und gilt altersunabhängig (§ 15 BBiG seit dem
 * BBiMoG 2020). Ein volljähriger Azubi bekommt also KEIN jugend_*-Flag, aber sehr wohl das BBiG-Flag.
 */
export function jarbschgPeriodViolations(days: readonly TimesheetDay[], opts: PeriodViolationOptions = {}): ArbZgFlag[] {
  if (days.length === 0) return []
  const holidays = opts.holidayDays ?? new Set<string>()
  const anomalyDays = opts.anomalyDays ?? new Set<string>()
  const flags: ArbZgFlag[] = []
  const sorted = [...days].sort((a, b) => a.dayKey.localeCompare(b.dayKey))
  const minorOn = (dayKey: string): boolean => isMinorOn(opts.birthdate, dayKey)
  const azubiOn = (dayKey: string): boolean =>
    opts.ausbildungVon != null && dayKey >= opts.ausbildungVon && (opts.ausbildungBis == null || dayKey <= opts.ausbildungBis)

  // ---- TAGESREGELN ----
  for (const d of sorted) {
    const minor = minorOn(d.dayKey)
    const start = dayStartMs(d.dayKey)
    const end = start + DAY_MS - 1000
    // ⚠️ Eine Anomalie (vermutlich vergessenes Ausstempeln) macht die DAUER unzuverlässig — nicht die
    // Tatsache, DASS gearbeitet wurde. Bei Sonntag/Feiertag/Schultag ist genau diese Tatsache der Verstoß,
    // deshalb wird hier nicht übersprungen, sondern nur markiert. (Der frühere `continue` hätte ausgerechnet
    // die Sonntagsarbeit unsichtbar gemacht, sobald jemand das Ausstempeln vergisst.)
    const anomal = anomalyDays.has(d.dayKey)
    // ⚠️ Bei einer Anomalie liefert timesheetDays für den Tag NULL Sekunden (workedSecondsByLocalDay
    // verwirft Segmente jenseits der 16-h-Kappe als unbrauchbar). Für die TATSACHEN-Regeln reicht das nicht:
    // eine Anomalie entsteht nur, WEIL jemand gestempelt hat — die Anwesenheit steht also fest, allein ihre
    // Dauer nicht. Ohne diese Zeile bliebe der Sonntagsschutz genau dort aus, wo Runde 1 ihn reparieren
    // wollte (der Fix „markieren statt überspringen" lief sonst ins Leere).
    const gearbeitet = d.workedSeconds > 0 || anomal

    // § 17 JArbSchG — Sonntagsruhe. Die Ausnahmen des Abs. 2 sind ABSCHLIESSEND ("nur … 1. bis 8.") und
    // nennen Reparaturwerkstätten für Kraftfahrzeuge NICHT — anders als § 16 Abs. 2 Satz 1 Nr. 11 für den
    // Samstag. Die Asymmetrie ist gewollt; § 10 ArbZG hilft nicht (§ 18 Abs. 2 ArbZG: für unter 18-Jährige
    // gilt das JArbSchG ANSTELLE des ArbZG). Deshalb hart, ohne Ausnahme-Suggestion im Text.
    if (minor && gearbeitet && new Date(start).getUTCDay() === 0) {
      flags.push({ type: 'jugend_sonntag', shiftStartMs: start, shiftEndMs: end, periodKind: 'day', periodKey: d.dayKey, workedSeconds: d.workedSeconds, anomaly: anomal })
    }
    // § 18 JArbSchG — Feiertagsruhe. Abs. 2 verweist auf § 17 Abs. 2, NICHT auf § 16 Abs. 2: die
    // Samstags-Privilegierung der Kfz-Werkstatt hilft am Feiertag also nicht.
    if (minor && gearbeitet && holidays.has(d.dayKey) && new Date(start).getUTCDay() !== 0) {
      flags.push({ type: 'jugend_feiertag', shiftStartMs: start, shiftEndMs: end, periodKind: 'day', periodKey: d.dayKey, workedSeconds: d.workedSeconds, anomaly: anomal })
    }
    // § 15 Abs. 1 Satz 2 Nr. 2 BBiG — nach einem GANZTÄGIGEN Berufsschultag darf der Azubi an diesem Tag
    // nicht mehr im Betrieb beschäftigt werden. ALTERSUNABHÄNGIG (§ 15 BBiG seit dem BBiMoG 2020).
    // ⚠️ Am ausbildungFactor festgemacht, nicht an creditedSeconds: an einem planfreien Tag entsteht keine
    // Gutschrift, der Schultag hat aber trotzdem stattgefunden. Und nicht an absenceGroup (nur die führende
    // Wirkung) — ein halber Urlaubstag daneben würde ihn sonst verdecken.
    if (azubiOn(d.dayKey) && gearbeitet && d.ausbildungFactor >= 1) {
      flags.push({
        type: 'azubi_schultag_beschaeftigt',
        shiftStartMs: start,
        shiftEndMs: end,
        periodKind: 'day',
        periodKey: d.dayKey,
        workedSeconds: d.workedSeconds,
        anomaly: anomal,
      })
    }
    // § 9 Abs. 1 Satz 2 Nr. 2 JArbSchG — der altersbasierte Zwilling: für Minderjährige gilt dasselbe
    // Beschäftigungsverbot, und zwar UNABHÄNGIG davon, ob ein Ausbildungsverhältnis gepflegt ist (der
    // Berufsschulpflichtige ohne erfassten Vertrag fiele sonst durch jedes Raster).
    if (minor && !azubiOn(d.dayKey) && gearbeitet && d.ausbildungFactor >= 1) {
      flags.push({
        // EIGENER Typ: derselbe Tatbestand, aber eine andere Norm. Ein Nicht-Azubi bekaeme sonst § 15 BBiG
        // zitiert, der auf ihn gar nicht anwendbar ist — und die dokumentierte Achsen-Trennung (Alter vs.
        // Ausbildungsvertrag) waere im Flag-Typ nicht mehr ablesbar.
        type: 'jugend_schultag_beschaeftigt',
        shiftStartMs: start,
        shiftEndMs: end,
        periodKind: 'day',
        periodKey: d.dayKey,
        workedSeconds: d.workedSeconds,
        anomaly: anomal,
      })
    }
  }

  // ---- WOCHENREGELN (§ 4 Abs. 4 Satz 1: Montag bis einschließlich Sonntag) ----
  const byWeek = new Map<string, TimesheetDay[]>()
  for (const d of sorted) byWeek.set(d.weekStart, [...(byWeek.get(d.weekStart) ?? []), d])
  for (const [weekStart, wDays] of byWeek) {
    // Eine Woche, deren Tage sämtlich anomal sind, sagt nichts aus; einzelne anomale Tage machen die
    // Wochensumme unzuverlässig → als Hinweis markieren statt zu unterdrücken.
    const anomal = wDays.some((d) => anomalyDays.has(d.dayKey))
    // ⚠️ NUR die Tage zählen, an denen der Beschäftigte noch minderjährig WAR. In der Geburtstagswoche —
    // die jeder Azubi genau einmal durchläuft — würden sonst die bereits volljährigen Tage mitsummiert und
    // ein Falschalarm entstünde. Der verbrennt zusätzlich den Dedup-Schlüssel für diese Periode.
    const jugendTage = wDays.filter((d) => minorOn(d.dayKey))
    if (jugendTage.length === 0) continue
    const start = dayStartMs(weekStart)
    const end = start + 7 * DAY_MS - 1000
    // Vollständig heißt: alle sieben Tage der Woche liegen im Rechenbereich UND gehören zur Jugendzeit.
    const partial = wDays.length < 7 || jugendTage.length < wDays.length

    const summe = jugendTage.reduce((s, d) => s + jarbschgArbeitszeit(d, holidays), 0)
    if (summe > JARBSCHG_MAX_WEEK_SECONDS) {
      flags.push({
        type: 'jugend_week_over_40h',
        shiftStartMs: start,
        shiftEndMs: end,
        periodKind: 'week',
        periodKey: weekStart,
        workedSeconds: summe,
        partial,
        anomaly: anomal,
      })
    }
    const tage = jugendTage.filter(istBeschaeftigungstag).length
    if (tage > JARBSCHG_MAX_DAYS_PER_WEEK) {
      flags.push({
        type: 'jugend_week_over_5_days',
        shiftStartMs: start,
        shiftEndMs: end,
        periodKind: 'week',
        periodKey: weekStart,
        count: tage,
        partial,
        anomaly: anomal,
      })
    }
  }

  // ---- MONATSREGEL § 16 Abs. 2 Satz 2 (SOLL-Vorschrift → Hinweis, nicht bußgeldbewehrt) ----
  const byMonth = new Map<string, TimesheetDay[]>()
  for (const d of sorted) byMonth.set(monthOf(d.dayKey), [...(byMonth.get(monthOf(d.dayKey)) ?? []), d])
  for (const [period, mDays] of byMonth) {
    if (!mDays.some((d) => minorOn(d.dayKey))) continue
    // ⚠️ Die Gesamtzahl der Samstage kommt aus dem KALENDER, nicht aus dem übergebenen Ausschnitt. Zählte
    // man sie aus den Tagen, ergäbe eine Teilperiode (Cron am 6. des Monats sieht einen Samstag) die
    // Schwelle max(0, 1−2) = 0 und der erste gearbeitete Samstag wäre sofort ein „Verstoß" — der dann auch
    // noch den Dedup-Schlüssel für den Rest des Monats verbraucht.
    const samstage = saturdaysInMonth(period)
    const gesamt = samstage.length
    const alle = new Set(mDays.map((d) => d.dayKey))
    const vollstaendig = samstage.every((s) => alle.has(s))
    if (!vollstaendig) continue // ohne alle Samstage im Blick ist die Aussage wertlos
    // Auch hier nur die Tage der Jugendzeit (Geburtstag im Monat).
    const beschaeftigt = mDays.filter((d) => minorOn(d.dayKey) && new Date(dayStartMs(d.dayKey)).getUTCDay() === 6 && d.workedSeconds > 0).length
    const erlaubt = Math.max(0, gesamt - JARBSCHG_FREE_SATURDAYS_PER_MONTH)
    if (beschaeftigt > erlaubt) {
      const start = dayStartMs(period + '-01')
      flags.push({
        type: 'jugend_samstage_im_monat',
        shiftStartMs: start,
        shiftEndMs: start + 31 * DAY_MS,
        periodKind: 'month',
        periodKey: period,
        count: beschaeftigt,
        advisory: true,
      })
    }
  }

  return flags.sort((a, b) => a.shiftStartMs - b.shiftStartMs || a.type.localeCompare(b.type))
}

/**
 * Tage, an denen § 8 Abs. 2a JArbSchG ausnahmsweise 8,5 h erlaubt: in derselben Woche (Mo–So) ist an
 * mindestens einem anderen WERKTAG mit Beschäftigung die Arbeitszeit auf unter 8 h verkürzt.
 *
 * Ohne diese Ausnahme meldet der Tages-Wächter genau den Regelfall eines Azubis als Verstoß: Mo–Do je
 * 8,5 h und Fr 6 h sind 40 h und vollständig legal — der Schicht-Motor sähe vier Verstöße. Er kann die
 * Ausnahme selbst nicht kennen, weil sie Wochenwissen braucht; deshalb reicht der Aufrufer sie hinein.
 */
export function ausgleichsTageJArbSchG(days: readonly TimesheetDay[], holidayDays: ReadonlySet<string> = new Set()): Set<string> {
  const byWeek = new Map<string, TimesheetDay[]>()
  for (const d of days) byWeek.set(d.weekStart, [...(byWeek.get(d.weekStart) ?? []), d])
  const out = new Set<string>()
  for (const [, wDays] of byWeek) {
    // Werktag = Mo–Sa (§ 3 Abs. 2 ArbZG-Verständnis; der Sonntag ist für Jugendliche ohnehin gesperrt).
    // Sonntage sind für Jugendliche ohnehin gesperrt; FEIERTAGE zählen hier ebenfalls nicht: § 8 Abs. 2a
    // meint einen Werktag, an dem die ARBEITSZEIT verkürzt wurde — nicht einen Tag, an dem wegen des
    // Feiertags gar nicht gearbeitet wird. (Seit die Feiertags-Ausfallzeit in jarbschgArbeitszeit einfließt,
    // sähe ein Feiertag auf einem kurzen Plantag sonst wie ein Ausgleichstag aus und höbe die 8-h-Grenze
    // für die ganze Woche auf — Review-Runde 2.)
    const werktage = wDays.filter((d) => new Date(dayStartMs(d.dayKey)).getUTCDay() !== 0 && !holidayDays.has(d.dayKey))
    const hatKurzenTag = werktage.some((d) => {
      const az = jarbschgArbeitszeit(d, holidayDays)
      return az > 0 && az < JARBSCHG_MAX_WORK_SECONDS
    })
    if (hatKurzenTag) for (const d of werktage) out.add(d.dayKey)
  }
  return out
}

/**
 * Flag-Typen, die ein Art.-9-Gesundheitsdatum offenbaren — sichtbar nur für den Betroffenen selbst und den
 * Freigabe-Kreis. Bewusst eine EXPLIZITE Liste statt eines Präfix-Filters: eine Blacklist auf 'muschg_' ist
 * fail-open, jede neue Flag-Familie wäre automatisch für jeden mit `arbeitszeit:lesen` sichtbar.
 *
 * `jugend_*` und `azubi_*` stehen hier bewusst NICHT: Jugendschutz ist kein Gesundheitsdatum, und der
 * Meister MUSS die Grenzen kennen, um sie einhalten zu können (Art. 6 Abs. 1 lit. c DSGVO, § 26 BDSG).
 */
export const SENSITIVE_FLAG_TYPES: ReadonlySet<ArbZgFlagType> = new Set<ArbZgFlagType>(['muschg_over_8_5h', 'muschg_nachtarbeit'])

// ---- § 3 ArbZG — DURCHSCHNITTSWÄCHTER (docs/DESIGN-zeit-arbzg-durchschnitt.md) ----
//
// § 3 ArbZG, vollständig (amtlicher Wortlaut, geprüft 29.07.2026):
//   „Die werktägliche Arbeitszeit der Arbeitnehmer darf acht Stunden nicht überschreiten. Sie kann auf
//    bis zu zehn Stunden nur verlängert werden, wenn innerhalb von sechs Kalendermonaten oder innerhalb
//    von 24 Wochen im Durchschnitt acht Stunden werktäglich nicht überschritten werden."
//
// Der Schicht-Motor kennt nur Satz 2 als absolute Obergrenze (`over_10h`). Satz 1 ist die eigentliche
// Regel, und ihre BEDINGUNG ist ein Durchschnitt über Monate — die kann eine Schicht-Prüfung strukturell
// nicht sehen. Ohne diesen Wächter fährt ein Betrieb dauerhaft 9,5-h-Tage, ohne dass je ein Alarm kommt.

/** Ausgleichszeitraum nach § 3 Satz 2 — der Arbeitgeber darf zwischen beiden wählen. */
export const AUSGLEICHSZEITRAEUME = ['24_wochen', '6_monate'] as const
export type Ausgleichszeitraum = (typeof AUSGLEICHSZEITRAEUME)[number]
export function isAusgleichszeitraum(v: unknown): v is Ausgleichszeitraum {
  return typeof v === 'string' && (AUSGLEICHSZEITRAEUME as readonly string[]).includes(v)
}

/** § 3 Satz 1: acht Stunden je Werktag — der Wert, mit dem das Budget gebildet wird. */
export const ARBZG_AVG_DAILY_SECONDS = 8 * 3600
/**
 * Frühwarnschwelle. ⚠️ PRODUKT-Entscheidung, KEINE Rechtsgrenze — im UI auch so benannt.
 * Kalibrierung: die 5-Tage-/40-h-Woche liegt bei 40/48 = 83 % (der freie Samstag ist ein Werktag mit
 * null Stunden und erzeugt die Luft). Gelb greift damit erst ab ⌀ 43,2 h/Woche, Rot ab ⌀ 48 h/Woche —
 * der Normalbetrieb löst nichts aus, ein dauerhaftes Überstunden-Regime schon.
 */
export const ARBZG_AVG_WARN_RATIO = 0.9

export type AverageLevel = 'gruen' | 'gelb' | 'rot'

export interface AverageWindow {
  variant: Ausgleichszeitraum
  /** Tatsächlich gerechneter Bereich (bereits auf Datenlage und Anspruchsbeginn geklemmt). */
  from: string
  to: string
  /** Werktage im Bereich: Mo–Sa ohne gesetzliche Feiertage (§ 3 Abs. 2 BUrlG als Legaldefinition). */
  werktage: number
  /** Summe der neutral gestellten Anteile (Urlaub/Krank/unbezahlt …) an diesen Werktagen. */
  neutraleTage: number
  /** Nenner der Durchschnittsbildung: werktage − neutraleTage. */
  anrechenbareWerktage: number
  budgetSeconds: number
  /** Zähler: gestempelte Zeit + Berufsschulzeit, über ALLE Kalendertage (inkl. Sonn-/Feiertag). */
  workedSeconds: number
  /** budget − worked. Negativ = Grenze gerissen. */
  headroomSeconds: number
  /** worked / budget; null, wenn es nichts zu messen gibt (Nenner 0). */
  utilization: number | null
  level: AverageLevel
  /** Fenster nicht vollständig gedeckt (Eintritt/18. Geburtstag/Datenlage) → Zwischenstand, kein Verstoß. */
  partial: boolean
  /** Tage mit unsicherer Zeit (vergessenes Ausstempeln) im Bereich — Sonn- und Feiertage eingeschlossen.
   *  Die WERKTAGE darunter sind aus `werktage` herausgerechnet: ein unbeurteilbarer Tag darf kein
   *  Ausgleichs-Budget schaffen. Bekannte Stunden bleiben im Zähler; `workedSeconds` ist eine UNTERgrenze. */
  unsichereTage: number
}

export interface AverageGuardResult {
  /** Beide Varianten — oder nur die eine, die die Werkstatt nach § 7 festgelegt hat. */
  windows: AverageWindow[]
  /** Gesamtbewertung über die gerechneten Fenster. */
  level: AverageLevel
  /**
   * Genau EIN Fenster liegt über der Linie. Dann hängt die Antwort davon ab, welchen Ausgleichszeitraum
   * die Werkstatt gewählt hat (§ 3 Satz 2 stellt beide zur Wahl) — ehrlicher Zwischenzustand statt
   * Münzwurf. Verschwindet, sobald `ausgleichszeitraum` in den Einstellungen gesetzt ist.
   */
  ambiguous: boolean
}

export interface AverageGuardOptions {
  /** Festgelegter Ausgleichszeitraum (§ 7 ArbZG). Fehlt er, werden BEIDE gesetzlichen Varianten gerechnet. */
  ausgleichszeitraum?: Ausgleichszeitraum | null
  /** Gesetzliche Feiertage im Bereich — sie sind KEINE Werktage und kürzen damit das Budget. */
  holidayDays?: ReadonlySet<string>
  /** Tage mit anomaler Schicht: dort liefert timesheetDays 0 s, die Summe unterzählt also. */
  anomalyDays?: ReadonlySet<string>
  /**
   * Frühestes Datum, ab dem § 3 ArbZG für diese Person überhaupt greift — Maximum aus Eintrittsdatum und
   * dem 18. Geburtstag (§ 18 Abs. 2 ArbZG: für unter 18-Jährige gilt das JArbSchG ANSTELLE des ArbZG).
   * Klemmt den Fensteranfang und setzt `partial`.
   */
  eligibleFrom?: string | null
  /** Bezugstag (Fensterende). Default: der letzte Tag in `days`. */
  today?: string
}

/** Kalendertag n Monate vor `dayKey`, mit Klemmung auf die Monatslänge (31.08. − 6 Monate → 28.02.). */
function minusMonths(dayKey: string, n: number): string {
  const y = Number(dayKey.slice(0, 4))
  const m = Number(dayKey.slice(5, 7))
  const d = Number(dayKey.slice(8, 10))
  const total = y * 12 + (m - 1) - n
  const ny = Math.floor(total / 12)
  const nm = ((total % 12) + 12) % 12
  const lastDay = new Date(Date.UTC(ny, nm + 1, 0)).getUTCDate()
  return `${String(ny).padStart(4, '0')}-${String(nm + 1).padStart(2, '0')}-${String(Math.min(d, lastDay)).padStart(2, '0')}`
}

const addDaysKey = (dayKey: string, n: number): string => utcDayKey(dayStartMs(dayKey) + n * DAY_MS)

/**
 * Erster Tag des Fensters einer Variante, EINSCHLIESSLICH `today` als letztem Tag.
 *
 * Exportiert, damit Handler und Cron exakt den Bereich laden, den der Motor auch rechnet. Läge die
 * Datums-Arithmetik zweimal im Code, driftete der geladene Bereich irgendwann vom gerechneten ab — und die
 * Folge wäre ein stilles `partial`, das niemand als Fehler erkennt.
 */
export function arbzgWindowFrom(today: string, variant: Ausgleichszeitraum): string {
  return variant === '24_wochen' ? addDaysKey(today, -(24 * 7 - 1)) : addDaysKey(minusMonths(today, 6), 1)
}

/** Frühester Fensteranfang über alle zu rechnenden Varianten — der Bereich, den der Aufrufer laden muss. */
export function arbzgLoadFrom(today: string, ausgleichszeitraum?: Ausgleichszeitraum | null): string {
  const varianten = ausgleichszeitraum != null ? [ausgleichszeitraum] : [...AUSGLEICHSZEITRAEUME]
  return varianten.map((v) => arbzgWindowFrom(today, v)).reduce((a, b) => (b < a ? b : a))
}

/**
 * ARBEITSZEIT im Sinne des § 2 Abs. 1 ArbZG für einen Tag — gestempelte Zeit plus Berufsschulzeit.
 *
 * ⚠️ BEWUSST NICHT jarbschgArbeitszeit(): jener Motor rechnet zusätzlich die am Feiertag AUSGEFALLENE
 * Arbeitszeit mit, weil § 4 Abs. 4 Satz 2 JArbSchG das ausdrücklich anordnet. Für das ArbZG gibt es keine
 * solche Anrechnungsnorm — dort ist der Feiertag schlicht kein Werktag und taucht im Nenner gar nicht erst
 * auf. Wer die Funktion aus Bequemlichkeit wiederverwendet, baut eine Feiertags-Gutschrift ein, die das
 * Gesetz nicht kennt, und verschiebt jede Feiertagswoche in Richtung Alarm.
 *
 * Berufsschulzeit zählt mit (strittig, bewusst entschieden — DESIGN 2.3): wer acht Stunden Unterricht
 * hatte und danach vier Stunden in der Halle steht, hat real zwölf Stunden hinter sich, und genau diese
 * Belastung begrenzt § 3. Zudem rechnet der JArbSchG-Motor bereits so — zwei verschiedene
 * Arbeitszeit-Begriffe für denselben Tag wären die Drift, die wir im Azubi-Block teuer ausgebaut haben.
 */
function arbzgArbeitszeit(d: TimesheetDay): number {
  return d.workedSeconds + d.creditedAusbildungSeconds
}

function levelOf(utilization: number | null, workedSeconds: number): AverageLevel {
  // Nenner 0 (der ganze Bereich neutral gestellt): es gibt nichts zu messen. Wurde trotzdem gestempelt,
  // ist das ein ERFASSUNGS-Widerspruch (dafür gibt es absenceStampConflicts) — hier reicht ein Gelb, damit
  // der Fall nicht lautlos als „unauffällig" durchgeht.
  if (utilization == null) return workedSeconds > 0 ? 'gelb' : 'gruen'
  if (utilization > 1) return 'rot'
  if (utilization >= ARBZG_AVG_WARN_RATIO) return 'gelb'
  return 'gruen'
}

function computeWindow(
  variant: Ausgleichszeitraum,
  sorted: readonly TimesheetDay[],
  today: string,
  holidays: ReadonlySet<string>,
  anomalies: ReadonlySet<string>,
  eligibleFrom: string | null | undefined,
  aktivAb: string | undefined,
): AverageWindow {
  const sollFrom = arbzgWindowFrom(today, variant)
  const dataFrom = sorted[0]!.dayKey
  const dataTo = sorted[sorted.length - 1]!.dayKey
  // ⚠️ Der Bereich wird auf die DATENLAGE geklemmt, nicht auf den Sollbereich aufgefüllt. Zählte man
  // Werktage aus dem Kalender, die gar keine Daten haben, entstünde Budget aus dem Nichts: vier Wochen
  // Daten in einem 24-Wochen-Fenster ergäben 1.152 h Budget bei 160 h Arbeit — immer grün. Das ist
  // fail-open, der schlimmste Fehlermodus für einen Schutzwächter. Stattdessen: kleineres Fenster +
  // `partial`.
  //
  // ⚠️ `aktivAb` schließt dieselbe Lücke für den Fall, dass das EINTRITTSDATUM nicht gepflegt ist (es ist
  // ein optionales Feld). timesheetDays liefert JEDEN Kalendertag des angefragten Bereichs, auch die vor
  // dem ersten Arbeitstag — `dataFrom` allein greift hier also nie, und ein seit drei Wochen beschäftigter
  // Mitarbeiter bekäme 24 Wochen Budget geschenkt und sähe monatelang unauffällig aus. Vor dem ersten
  // erfassten Vorgang gibt es keinen Beleg dafür, dass das Arbeitsverhältnis überhaupt bestand.
  const from = [sollFrom, dataFrom, eligibleFrom ?? '', aktivAb ?? ''].reduce((a, b) => (b > a ? b : a))
  const to = today < dataTo ? today : dataTo
  const partial = from > sollFrom || to < today

  let werktage = 0
  let neutraleTage = 0
  let workedSeconds = 0
  let unsichereTage = 0
  for (const d of sorted) {
    if (d.dayKey < from || d.dayKey > to) continue
    // ZÄHLER über ALLE Kalendertage: Sonntags- und Feiertagsarbeit bleibt Arbeitszeit (§ 2 Abs. 1); § 11
    // Abs. 3 gleicht sie mit einem Ersatzruhetag aus, nimmt die Stunden aber nicht aus der Rechnung.
    workedSeconds += arbzgArbeitszeit(d)
    // ⚠️ VOR der Werktags-Prüfung zählen: sonst fiele ausgerechnet eine Anomalie am SONNTAG unter den
    // Tisch — und Sonntagsarbeit ist der Fall, der den Schnitt am stärksten verschlechtert. Sie liefert
    // dann null Stunden UND keinen Hinweis darauf, dass da etwas war.
    const unsicher = anomalies.has(d.dayKey)
    if (unsicher) unsichereTage++
    // NENNER nur über Werktage: Mo–Sa ohne gesetzliche Feiertage. Die Asymmetrie ist gewollt — am Sonntag
    // gearbeitete Stunden verschlechtern den Schnitt doppelt, weil der Tag kein Budget beisteuert. Genau
    // das ist der Sinn: Sonntagsarbeit soll die Ausnahme bleiben.
    const jsDay = new Date(dayStartMs(d.dayKey)).getUTCDay() // 0 = Sonntag
    if (jsDay === 0 || holidays.has(d.dayKey)) continue
    // ⚠️ Ein Werktag mit vergessenem Ausstempeln steuert KEIN Budget bei. timesheetDays verwirft das
    // Segment jenseits der 16-h-Kappe und liefert null Sekunden — der Tag schüfe also 8 h Budget und
    // stellte null Arbeit dagegen. Doppelt fail-open: der Zähler unterzählt UND der Nenner wächst.
    // Richtig ist „wir wissen es nicht": bekannte Stunden zählen (oben, im Zähler), ein unbeurteilbarer
    // Tag schafft aber kein Guthaben.
    if (unsicher) continue
    werktage++
    neutraleTage += d.absenceNeutralFactor
  }

  const anrechenbareWerktage = Math.max(0, werktage - neutraleTage)
  const budgetSeconds = Math.round(anrechenbareWerktage * ARBZG_AVG_DAILY_SECONDS)
  const utilization = budgetSeconds > 0 ? workedSeconds / budgetSeconds : null
  return {
    variant,
    from,
    to,
    werktage,
    neutraleTage: Math.round(neutraleTage * 100) / 100,
    anrechenbareWerktage: Math.round(anrechenbareWerktage * 100) / 100,
    budgetSeconds,
    workedSeconds,
    headroomSeconds: budgetSeconds - workedSeconds,
    utilization,
    level: levelOf(utilization, workedSeconds),
    partial,
    unsichereTage,
  }
}

/**
 * § 3 ArbZG-Durchschnittswächter über ein rollierendes Fenster. I/O-frei; Eingabe ist ausschließlich der
 * Output von timesheetDays().
 *
 * ROLLIEREND, rückwärts von `today` — nicht fest verankert. Die Aufsicht prüft einen beliebigen
 * Zeitraum-Ausschnitt; ein fest verankertes Fenster könnte eine Überschreitung dadurch verstecken, dass
 * sie genau über die Periodengrenze fällt. Außerdem braucht ein rollierendes Fenster keinen Startpunkt,
 * den die Werkstatt nie festgelegt hat.
 *
 * ⚠️ NUR FÜR VOLLJÄHRIGE. § 18 Abs. 2 ArbZG: für unter 18-Jährige gilt das JArbSchG ANSTELLE des ArbZG.
 * Der Aufrufer klemmt das über `eligibleFrom` (18. Geburtstag bzw. Eintritt).
 *
 * ⚠️ `partial` heißt: KEIN Verstoß, sondern Zwischenstand. § 3 verlangt den Ausgleich INNERHALB des
 * Zeitraums — solange der noch läuft, kann jeder Kurztag ihn retten. Dieselbe Falle hat im Azubi-Block
 * § 8 Abs. 2a JArbSchG gestellt: punktuell ausgewertet alarmiert eine Ausgleichsregel systematisch zu
 * früh, weil der ausgleichende Kurztag noch in der Zukunft liegt.
 */
export function arbzgAverageWindow(days: readonly TimesheetDay[], opts: AverageGuardOptions = {}): AverageGuardResult {
  const empty: AverageGuardResult = { windows: [], level: 'gruen', ambiguous: false }
  if (days.length === 0) return empty
  const sorted = [...days].sort((a, b) => a.dayKey.localeCompare(b.dayKey))
  const today = opts.today ?? sorted[sorted.length - 1]!.dayKey
  const eligibleFrom = opts.eligibleFrom
  // Anspruchsbeginn nach dem Bezugstag (z. B. 18. Geburtstag liegt noch in der Zukunft): nichts zu prüfen.
  if (eligibleFrom != null && eligibleFrom > today) return empty
  const holidays = opts.holidayDays ?? new Set<string>()
  const anomalies = opts.anomalyDays ?? new Set<string>()

  // Erster Tag mit irgendeinem erfassten Vorgang — Stempel, Berufsschulzeit oder Abwesenheit. Dient als
  // Ersatz-Eintrittsdatum, wenn keines gepflegt ist (siehe computeWindow). Eine Abwesenheit zählt bewusst
  // mit: wer Urlaub eingetragen hat, war beschäftigt.
  const aktivAb = sorted.find((d) => d.workedSeconds > 0 || d.creditedAusbildungSeconds > 0 || d.absenceNeutralFactor > 0)?.dayKey
  const varianten: Ausgleichszeitraum[] = opts.ausgleichszeitraum != null ? [opts.ausgleichszeitraum] : [...AUSGLEICHSZEITRAEUME]
  const windows = varianten.map((v) => computeWindow(v, sorted, today, holidays, anomalies, eligibleFrom, aktivAb))
  if (windows.length === 0) return empty

  const rote = windows.filter((w) => w.level === 'rot').length
  // Beide Fenster über der Linie → Verstoß, egal welchen Ausgleichszeitraum die Werkstatt gewählt hat.
  // Nur eines → die Antwort hängt an der (uns unbekannten) Wahl nach § 7: ehrliches Gelb statt Münzwurf.
  const ambiguous = windows.length > 1 && rote === 1
  const level: AverageLevel =
    rote === windows.length ? 'rot' : rote > 0 || windows.some((w) => w.level === 'gelb') ? 'gelb' : 'gruen'
  return { windows, level, ambiguous }
}
